import Action from 'actions';
import BaseExecutor from 'services/sports/default/base-executor';
import {countEntries, mergeMultidimensionalArr, groupObjectsByProp, getPropValues} from 'helpers/array-helper';
import Const from 'constants';
import {
    isPlayerOnDoublePosition,
    isPlayerOnPosition,
    getPlayerUIdsByParams,
    preparePlayerUID,
} from 'helpers/player-helper';
import {calcQuantityOfReservedLineupsByExposure, countPlayerIdInLineups} from 'helpers/lineup-helper';
import {calcPercentage, calcAndRoundDownValByPct, calcAndRoundUpValByPct} from 'helpers/common-helper';
import {sortByProperty} from 'helpers/sorters';
import {
    getPlayersGroupUID,
    getCSComboVarName,
    addRoleSuffix,
    extractEntityIdFromSimplexItem,
    getSimplexKeyUnitName,
    getSimplexUnitName,
    getSimplexUnitNamesByParams,
    getStackTypeNumberByType,
} from 'helpers/simplex-helper';
import NumberHelper, {avg, toFloat} from 'helpers/number-helper';
import RandomTeamsStackMarker from 'components/organisms/random-team-stacking/services/random-teams-stack-marker';
import CompetitionStackMarker from 'components/organisms/competition-stacking/services/competition-stack-marker';
import KeyPlayerStackMarker from 'services/key-player-stacking/key-player-stack-marker';
import CorrelationStackLineupAnalyzer from 'components/organisms/correlation-stacking/services/correlation-stack-lineup-analyzer';
import DoubleSecondaryStackCompatibleTypesRulesBuilder from 'components/organisms/correlation-stacking/services/double-secondary-stack-compatible-types-rules-builder';
import RuleBuilder from 'services/simplex/rule-builder';
import {RestrictedPlayersSimplexConstraintsBuilder} from 'components/organisms/correlation-stacking/services/restricted-players-simplex-constraints-builder';
import {Factory as RandomTeamsStackingConstraintsBuilderFactory} from 'components/organisms/random-team-stacking/services/constraints-builder-factory';
import {Factory as NonControlledHittersStackingConstraintsBuilderFactory} from 'components/organisms/non-controlled-hitter-stacking/services/constraints-builder-factory';
import {Factory as SinglegameForcedStackingRulesBuilderFactory} from 'components/organisms/singlegame-forced-stacking/services/rules-builder-factory';
import {Factory as CompetitionStackingConstraintsBuilderFactory} from 'components/organisms/competition-stacking/services/constraints-builder-factory';
import {Factory as KeyPlayerStackingConstraintsBuilderFactory} from 'services/key-player-stacking/constraints-builder-factory';
import {Factory as PlayerCombinationStackingConstrainsBuilderFactory} from 'components/organisms/player-combination-stacking/services/constraints-builder-factory';
import {StackAnalyzer as PlayerCombinationStackAnalyzer} from 'components/organisms/player-combination-stacking/services/stack-analyzer';
import {Factory as LineupAnalyzerFactory} from 'services/lineup-analyzer/lineup-analyzer-factory';
import {createSimplexCompetitionKey} from 'components/organisms/competition-stacking/helpers';
import KeyPlayerStackAnalyzer from 'services/key-player-stacking/key-player-stack-analyzer';
import {getPlayerService, getLpService} from 'services/sports/factory';
import {Factory as NhlTeamStackingConstraintsBuilderFactory} from 'components/organisms/nhl-team-stacking/services/constraints-builder-factory';
import {Factory as NhlLegacyTeamStackingConstraintsBuilderFactory} from 'components/organisms/nhl-legacy-team-stacking/services/constraints-builder-factory';
import {Factory as ProjectionBoostRuleConstraintsBuilderFactory} from 'components/organisms/projection-boost-rule/services/constraints-builder-factory';

export class Simplex extends BaseExecutor {
    constructor(incomingValues = {}) {
        super();
        const {store} = incomingValues;
        if (store) {
            this.store = store;
        }
    }

    stackRandomTeam({incomingValues: {modes}}) {
        const {
            projectionPointsType,
            sportConfig,
            lineupsQuantity,
            randomTeamStacking: stacking,
            playersData: {preparedPlayers},
            lineupTeamsCombinations,
            lineupsData: {rawLineups},
            lineupTeamsStacks,
        } = Action.getState();
        const mappedPlayersByTeams = preparedPlayers.groupedBy.property('team');

        let constraints = [];
        if (!stacking.isEnabled()) {
            return [...constraints];
        }

        const ConstraintsBuilder = RandomTeamsStackingConstraintsBuilderFactory.getBuilder(sportConfig);
        const constraintsBuilder = new ConstraintsBuilder({
            stacking,
            mappedPlayersByTeams,
            modes,
            lineupsQuantity: lineupsQuantity.getLineupsQuantity(),
            lineupTeamsCombinations,
            rawLineups,
        });

        const getPreparedTeamsConstraints = function () {
            let constraints = this.store.get('randomTeamsStackingConstraints', {});
            if (isEmpty(constraints)) {
                constraints = constraintsBuilder.createConstraintsForPlayersInTeam();
                this.store.set('randomTeamsStackingConstraints', {...constraints});
            }
            return {...constraints};
        }.bind(this);

        const usedMarkedTeams = function () {
            const groupsOfTeams = this.store.get('usedTeamGroups', []);
            const usedTeams = [],
                stackedTeams = [];
            groupsOfTeams.map((groupOfTeams) => {
                const usedMarkedTeams = RandomTeamsStackMarker.pickMarkedBySuffix(groupOfTeams);
                usedMarkedTeams.map((item) => {
                    stackedTeams.push(item);
                    usedTeams.push(extractEntityIdFromSimplexItem(item));
                });
            });
            return {usedTeams, stackedTeams};
        }.bind(this)();

        const excludeTeamsByExposureMax = function (teamsConstraints) {
            const lineups = this.store.get('lineups', []);
            const usedTeams = usedMarkedTeams.usedTeams;
            Object.keys(teamsConstraints).map((teamName) => {
                const expMax = stacking.getTeamMaxExposure(teamName);
                const usageQuantity = countEntries(teamName, usedTeams);
                const usagePercent = ((usageQuantity + 1) / (lineups.length + 1)) * 100;

                if (usagePercent > expMax) {
                    delete teamsConstraints[teamName];
                }
            });

            return {...teamsConstraints};
        }.bind(this);

        const getExcludedTeamsFromStacksByExposureMax = function (filteredTeams) {
            const lineups = this.store.get('lineups', []);
            const stackedTeams = usedMarkedTeams.stackedTeams;
            const excludedTeamsFromStacks = {1: [], 2: []};
            filteredTeams.map((teamName) => {
                [1, 2].map((groupNumber) => {
                    const expMax = lineupTeamsStacks.exposureMax.getValue(groupNumber, teamName);
                    if (100 <= expMax) {
                        return;
                    }

                    let groupName = getPlayersGroupUID(teamName, groupNumber);
                    groupName = RandomTeamsStackMarker.setSuffix(groupName);

                    const usageQuantity = countEntries(groupName, stackedTeams);
                    const usagePercent = calcPercentage(1 + lineups.length, 1 + usageQuantity);

                    if (usagePercent > expMax) {
                        excludedTeamsFromStacks[groupNumber].push(teamName);
                    }
                });
            });

            return {...excludedTeamsFromStacks};
        }.bind(this);

        const getProjectionPointsPropsByModes = function () {
            const Player = getPlayerService(sportConfig);
            const result = {};
            modes.map((playerMode) => {
                result[playerMode] = Player.exec('definePropNameOfMode', {
                    prop: projectionPointsType.getProjectionPointsType(),
                    mode: playerMode,
                });
            });
            return result;
        };

        const teamsConstraints = getPreparedTeamsConstraints();
        const filteredTeamsConstraints = excludeTeamsByExposureMax(teamsConstraints);
        const filteredTeams = Object.keys(filteredTeamsConstraints);

        constraintsBuilder.setAvailableTeams(filteredTeams);
        const excludedTeamsFromStacks = getExcludedTeamsFromStacksByExposureMax(filteredTeams);

        const desirableConstraints = constraintsBuilder.createConstraintsForDesirableTeams({
            usedTeams: usedMarkedTeams.usedTeams,
        });
        const teamInGroupsConstraints = constraintsBuilder.createConstraintsForSameTeamInStackingGroups();
        const teamsInGroupConstraints = constraintsBuilder.createConstraintsForTeamsInStackingGroup();
        const teamsCombinationsConstraints = constraintsBuilder.createConstraintsForTeamsCombinations();
        const excludedTeamsFromStacksConstraints = constraintsBuilder.createConstraintsForExcludedTeamsFromStacks({
            excludedTeamsFromStacks,
        });

        let forcedCaptainsConstraints = [];
        if (stacking.isCaptainForced()) {
            const constraints = constraintsBuilder.createConstraintsForForcedCaptains({
                groupNumber: 1,
                modes: [Const.CAPTAIN_MODE],
            });
            forcedCaptainsConstraints = Object.values(constraints);
        }

        let teamPlayersConstraints = [];
        Object.values(filteredTeamsConstraints).map((constraints) => {
            teamPlayersConstraints = [...teamPlayersConstraints, ...constraints];
        });

        constraints = [
            ...teamPlayersConstraints,
            ...forcedCaptainsConstraints,
            ...Object.values(teamInGroupsConstraints),
            ...Object.values(teamsInGroupConstraints),
            ...desirableConstraints,
            ...teamsCombinationsConstraints,
            ...excludedTeamsFromStacksConstraints,
        ];

        const teamsObjective = constraintsBuilder.createObjectiveForTeams();
        let playersObjective = {};
        if (stacking.isTeamsChosenRandomly()) {
            playersObjective = constraintsBuilder.createObjectiveForRandomlyChosenPlayers({
                projectionPointsPropNamesByModes: getProjectionPointsPropsByModes(),
            });
        }

        this.store.patchObj('additionalObjective', {
            'randomTeamsStacking.teamsObjective': teamsObjective,
            'randomTeamsStacking.playersObjective': playersObjective,
        });
        // console.log('constraints',{...constraints});
        return [...constraints];
    }

    max({incomingValues: {checkIfPlayerOnDoublePosition = false}}) {
        const {
            projectionPointsType,
            playersData: {preparedPlayers},
        } = Action.getState();
        const playersIndexedById = preparedPlayers.groupedBy.playerId();

        const rule = {row: {}};

        Object.values(playersIndexedById).map((player) => {
            const points = toFloat(player[projectionPointsType.getProjectionPointsType()], 3);

            const simplexUnitNames = getSimplexUnitNamesByParams({
                playerId: player.playerId,
                playersIndexedById,
                checkIfPlayerOnDoublePosition,
            });

            simplexUnitNames.map((simplexUnitName) => {
                rule.row[simplexUnitName] = points;
            });
        });

        return {...rule};
    }

    maxRandom({incomingValues: {checkIfPlayerOnDoublePosition = false}}) {
        const {
            projectionPointsType,
            playersData: {preparedPlayers},
            randomPoints,
            lineupsData: {rawLineups},
        } = Action.getState();
        const playersIndexedById = preparedPlayers.groupedBy.playerId();
        const lineupNumber = 1 + rawLineups.getQuantity();

        const rule = {row: {}};

        if (!randomPoints.items.hasOwnProperty(lineupNumber)) {
            return rule;
        }

        const pointsList = randomPoints.items[lineupNumber];

        Object.values(playersIndexedById).map((player) => {
            let points;
            if (pointsList.hasOwnProperty(player.playerId)) {
                points = pointsList[player.playerId] * 1;
            } else {
                points = toFloat(player[projectionPointsType.getProjectionPointsType()], 3);
            }

            const simplexUnitNames = getSimplexUnitNamesByParams({
                playerId: player.playerId,
                playersIndexedById,
                checkIfPlayerOnDoublePosition,
            });

            simplexUnitNames.map((simplexUnitName) => {
                rule.row[simplexUnitName] = points;
            });
        });
        // console.log("maxRandom\n", rule);
        return {...rule};
    }

    limitPositions({incomingValues: {checkIfPlayerOnDoublePosition = false}}) {
        const {
            sportConfig,
            playersData: {preparedPlayers},
        } = Action.getState();
        const playersIndexedById = preparedPlayers.groupedBy.playerId();
        const teams = groupObjectsByProp(playersIndexedById, 'team');
        const maxSingleTeamPlayers = sportConfig.getConfigProp('maxSingleTeamPlayers');

        const rules = [];

        if (!maxSingleTeamPlayers) {
            return rules;
        }

        Object.values(teams).map((team) => {
            const ruleBuilder = RuleBuilder.fromAtMost(maxSingleTeamPlayers);

            team.map((player) => {
                const simplexUnitNames = getSimplexUnitNamesByParams({
                    playerId: player.playerId,
                    playersIndexedById,
                    checkIfPlayerOnDoublePosition,
                });

                simplexUnitNames.map((simplexUnitName) => {
                    ruleBuilder.addVar(simplexUnitName, 1);
                });
            });

            if (ruleBuilder.isRuleCreatable()) {
                rules.push(ruleBuilder.createRule());
            }
        });

        return [...rules];
    }

    salary({incomingValues: {checkIfPlayerOnDoublePosition = false}}) {
        const {
            salaryCap,
            lineupGenerator: {settings},
            playersData: {preparedPlayers},
        } = Action.getState();
        const playersIndexedById = preparedPlayers.groupedBy.playerId();
        const isRelaxBuildRulesUsed = settings.isRelaxBuildRulesUsed();

        const ruleVars = {};
        Object.values(playersIndexedById).map((player) => {
            const simplexUnitNames = getSimplexUnitNamesByParams({
                playerId: player.playerId,
                playersIndexedById,
                checkIfPlayerOnDoublePosition,
            });
            simplexUnitNames.map((simplexUnitName) => {
                ruleVars[simplexUnitName] = player.salary;
            });
        });

        const rules = [];

        if (isEmpty(ruleVars)) {
            return rules;
        }

        let salaryMax = salaryCap.getInnerMax();
        if (isRelaxBuildRulesUsed) {
            salaryMax = salaryCap.getOuterMax();
        }

        const ruleBuilder = RuleBuilder.fromAtMost(salaryMax);
        ruleBuilder.addVars(ruleVars);

        rules.push(ruleBuilder.createRule());

        if (!isRelaxBuildRulesUsed && !salaryCap.isInnerMinDefault()) {
            const ruleBuilder = RuleBuilder.fromAtLeast(salaryCap.getInnerMin());
            ruleBuilder.addVars(ruleVars);

            rules.push(ruleBuilder.createRule());
        }
        // console.log("salary\n", rules);
        return [...rules];
    }

    projectedOwnership({incomingValues: {checkIfPlayerOnDoublePosition = false, modes = []}}) {
        const {
            projectedOwnershipCap,
            lineupGenerator: {settings},
            playersData: {preparedPlayers},
        } = Action.getState();
        const players = preparedPlayers.groupedBy.playerId();
        const isRelaxBuildRulesUsed = settings.isRelaxBuildRulesUsed();

        function isPlayerOnSinglePosition(playerId) {
            if (!checkIfPlayerOnDoublePosition) {
                return true;
            }
            return !(players.hasOwnProperty(playerId) && isPlayerOnDoublePosition(players[playerId]));
        }

        const projOwnMax = isRelaxBuildRulesUsed
            ? projectedOwnershipCap.getOuterMax()
            : projectedOwnershipCap.getInnerMax();

        const maxRuleBuilder = RuleBuilder.fromAtMost(projOwnMax);

        let row = {};
        Object.values(players).map((player) => {
            const pId = player.getId();

            if (!isPlayerOnSinglePosition(pId)) {
                player.position.map((pos) => {
                    row[getSimplexUnitName([pId, pos])] = player.projOwn.getValue();
                });

                return;
            }

            const simplexUnitName = getSimplexUnitName(pId);

            if (isEmpty(modes)) {
                row[simplexUnitName] = player.projOwn.getValue();

                return;
            }

            modes.map((mode) => {
                const simplexModeUnitName = addRoleSuffix({
                    prefix: simplexUnitName,
                    role: mode,
                });
                // row[simplexModeUnitName] = player.getPropValueOfMode('projected_ownership', mode);
                row[simplexModeUnitName] = player.projOwn.getValue(mode);
            });
        });

        maxRuleBuilder.addVars(row);
        const rules = [maxRuleBuilder.createRule()];

        if (!isRelaxBuildRulesUsed && !projectedOwnershipCap.isInnerMinDefault()) {
            const minRuleBuilder = RuleBuilder.fromAtLeast(projectedOwnershipCap.getInnerMin());
            minRuleBuilder.addVars(row);

            rules.push(minRuleBuilder.createRule());
        }
        // console.log("projOwnCap\n", rules);
        return [...rules];
    }

    forceOfProjectedOwnership({
        incomingValues: {checkIfPlayerOnDoublePosition = false, filterByPositions = true, modes = []},
    }) {
        const {
            projectedOwnershipForce,
            playersData,
            sportConfig,
            lineupGenerator: {settings},
        } = Action.getState();

        if (settings.isRelaxBuildRulesUsed() || !projectedOwnershipForce.isEnabled()) {
            return [];
        }

        const {preparedPlayers} = playersData;
        const players = preparedPlayers.groupedBy.playerId();
        const positions = Object.values(sportConfig.getPositions());
        const availablePositions = arrayDiff(positions, projectedOwnershipForce.getIgnoredPositions());

        function getAvailablePlayers() {
            let availablePlayers = Object.values(players);
            const subValue = projectedOwnershipForce.getSubValue();
            availablePlayers = availablePlayers.filter((player) => player.projOwn.getValue() <= subValue);

            if (!filterByPositions) {
                return availablePlayers;
            }

            return availablePlayers.filter((player) => isPlayerOnPosition(player, availablePositions));
        }

        function isPlayerOnSinglePosition(playerId) {
            if (!checkIfPlayerOnDoublePosition) {
                return true;
            }
            return !(players.hasOwnProperty(playerId) && isPlayerOnDoublePosition(players[playerId]));
        }

        const availablePlayers = getAvailablePlayers();
        const ruleBuilder = RuleBuilder.fromAtLeast(projectedOwnershipForce.getPlayersQuantity());

        availablePlayers.map((player) => {
            const pId = player.getId();
            if (isPlayerOnSinglePosition(pId)) {
                const simplexUnitName = getSimplexUnitName(pId);
                if (isEmpty(modes)) {
                    ruleBuilder.addVar(simplexUnitName, 1);
                } else {
                    modes.map((mode) => {
                        const simplexModeUnitName = addRoleSuffix({
                            prefix: simplexUnitName,
                            role: mode,
                        });
                        ruleBuilder.addVar(simplexModeUnitName, 1);
                    });
                }
            } else {
                player.position.map((pos) => {
                    ruleBuilder.addVar(getSimplexUnitName([pId, pos]), 1);
                });
            }
        });

        const result = [];
        if (ruleBuilder.isRuleCreatable()) {
            result.push(ruleBuilder.createRule());
        }

        return result;
    }

    games({incomingValues: {checkIfPlayerOnDoublePosition = false}}) {
        const {
            sportConfig,
            playersData: {preparedPlayers},
        } = Action.getState();
        const maxSingleGamePlayers = sportConfig.getConfigProp('playersInTeam') - 1;
        const playersByGames = preparedPlayers.groupedBy.property('gameInfo');
        const playersIndexedById = preparedPlayers.groupedBy.playerId();

        let rules = [];

        Object.values(playersByGames).map((players) => {
            if (isEmpty(players) || players.length <= maxSingleGamePlayers) {
                return;
            }

            const ruleBuilder = RuleBuilder.fromAtMost(maxSingleGamePlayers);

            players.map((player) => {
                const simplexUnitNames = getSimplexUnitNamesByParams({
                    playerId: player.playerId,
                    playersIndexedById,
                    checkIfPlayerOnDoublePosition,
                });

                simplexUnitNames.map((simplexUnitName) => {
                    ruleBuilder.addVar(simplexUnitName, 1);
                });
            });

            rules.push(ruleBuilder.createRule());
        });
        // console.log("games\n",[...rules]);
        return [...rules];
    }

    maxSingleGamePlayersByModes({incomingValues: {modes}}) {
        const {
            sportConfig,
            playersData: {preparedPlayers},
        } = Action.getState();
        const playersByGames = preparedPlayers.groupedBy.property('gameInfo');
        const maxSingleGamePlayers = sportConfig.getConfigProp('playersInTeam') - 1;

        const result = [];

        Object.values(playersByGames).map((players) => {
            const builder = RuleBuilder.fromAtMost(maxSingleGamePlayers);

            players.map((player) => {
                modes.map((playerMode) => {
                    const lpPlayerId = addRoleSuffix({
                        prefix: player.getId(),
                        role: playerMode,
                    });
                    builder.addVar(getSimplexUnitName(lpPlayerId), 1);
                });
            });

            if (builder.isRuleCreatable()) {
                result.push(builder.createRule());
            }
        });

        return [...result];
    }

    team({incomingValues: {checkIfPlayerOnDoublePosition = false}}) {
        const {
            sportConfig,
            playersData: {preparedPlayers},
            lineupsData: {filters: lineupsFilter},
            lineupGenerator: {settings},
        } = Action.getState();
        const defencePlayerPositions = sportConfig.getConfigProp('defencePlayerPositions');
        const maxSingleTeamPlayers = sportConfig.getConfigProp('maxSingleTeamPlayers');
        const playersGroupedByAllowedPositions = preparedPlayers.groupedBy.allowedPositions();
        const playersIndexedById = preparedPlayers.groupedBy.playerId();
        const teams = groupObjectsByProp(playersGroupedByAllowedPositions, 'team');

        let maxOppositeTeamDefencePlayersQty = null;
        if (lineupsFilter.opposingPlayers !== null) {
            if (settings.isRelaxBuildRulesUsed()) {
                const opposingPlayersCount = sportConfig.getConfigProp('opposingPlayersCount');
                if (opposingPlayersCount > 0) {
                    maxOppositeTeamDefencePlayersQty = maxSingleTeamPlayers - opposingPlayersCount;
                }
            } else {
                maxOppositeTeamDefencePlayersQty = maxSingleTeamPlayers - lineupsFilter.opposingPlayers;
            }
        }

        const rules = [];

        Object.values(teams).map((players) => {
            const ruleBuilder = RuleBuilder.fromAtMost(maxSingleTeamPlayers);
            // If value of opposingPlayers is not null, try to find a player from an opposite team.
            if (maxOppositeTeamDefencePlayersQty !== null && teams.hasOwnProperty(players[0].opponent)) {
                // If the rival team was found, iterate players from one
                // and define a needed player from defencePlayerPositions.
                const opponents = teams[players[0].opponent];
                Object.values(opponents).map((opponent) => {
                    //If the player from rival team was found, add him to teamStr.
                    if (!inArray(opponent.position, defencePlayerPositions)) {
                        return;
                    }

                    const simplexUnitNames = getSimplexUnitNamesByParams({
                        playerId: opponent.playerId,
                        playersIndexedById,
                        checkIfPlayerOnDoublePosition,
                    });

                    simplexUnitNames.map((simplexUnitName) => {
                        ruleBuilder.addVar(simplexUnitName, maxOppositeTeamDefencePlayersQty);
                    });
                });

                players.map((player) => {
                    if (inArray(player.position, defencePlayerPositions)) {
                        return;
                    }

                    const simplexUnitNames = getSimplexUnitNamesByParams({
                        playerId: player.playerId,
                        playersIndexedById,
                        checkIfPlayerOnDoublePosition,
                    });

                    simplexUnitNames.map((simplexUnitName) => {
                        ruleBuilder.addVar(simplexUnitName, 1);
                    });
                });
            } else {
                players.map((player) => {
                    const simplexUnitNames = getSimplexUnitNamesByParams({
                        playerId: player.playerId,
                        playersIndexedById,
                        checkIfPlayerOnDoublePosition,
                    });

                    simplexUnitNames.map((simplexUnitName) => {
                        ruleBuilder.addVar(simplexUnitName, 1);
                    });
                });
            }

            if (ruleBuilder.isRuleCreatable()) {
                rules.push(ruleBuilder.createRule());
            }
        });

        return [...rules];
    }

    minTeams({incomingValues = {}}) {
        const {playersData, sportConfig} = Action.getState();
        const {preparedPlayers} = playersData;
        const players = preparedPlayers.groupedBy.allowedPositions();
        const {unitIdPropName = 'playerId'} = incomingValues;
        const normalizeTeamName = (teamName) => teamName.replace('-', '_');

        const teams = {};
        players.map((player) => {
            const teamName = normalizeTeamName(player.team);
            !(teamName in teams) && (teams[teamName] = []);
            teams[teamName].push(player);
        });

        const rows = {};
        const rules = [];
        const minTeams = Number(sportConfig.getConfigProp('minTeams'));

        Object.keys(teams).map((teamName) => {
            const players = teams[teamName];
            rows[teamName] = {};
            players.map((player) => {
                rows[teamName][getSimplexUnitName(player[unitIdPropName])] = 1;
            });
            rows[teamName][getPlayersGroupUID(teamName)] = -1;
        });

        const teamsRow = {};
        const generalObjective = {};
        Object.keys(rows).map((teamName) => {
            teamsRow[getPlayersGroupUID(teamName)] = 1;
            generalObjective[getPlayersGroupUID(teamName)] = 0;

            const ruleBuilder = RuleBuilder.fromAtLeast(0);
            ruleBuilder.addVars({...rows[teamName]});

            rules.push(ruleBuilder.createRule());
        });

        const ruleBuilder = RuleBuilder.fromAtLeast(minTeams);
        ruleBuilder.addVars({...teamsRow});

        rules.push(ruleBuilder.createRule());

        this.store.patchObj('additionalObjective', {minTeams: {...generalObjective}});

        return [...rules];
    }

    player({incomingValues: {checkIfPlayerOnDoublePosition = false}}) {
        const {
            sportConfig,
            playersData: {preparedPlayers},
        } = Action.getState();
        const playersInTeam = sportConfig.getConfigProp('playersInTeam');
        const playersIndexedById = preparedPlayers.groupedBy.playerId();

        const ruleBuilder = RuleBuilder.fromExactly(playersInTeam);

        Object.keys(playersIndexedById).map((playerId) => {
            const simplexUnitNames = getSimplexUnitNamesByParams({
                playerId,
                playersIndexedById,
                checkIfPlayerOnDoublePosition,
            });
            simplexUnitNames.map((simplexUnitName) => {
                ruleBuilder.addVar(simplexUnitName, 1);
            });
        });

        const rules = [];
        if (ruleBuilder.isRuleCreatable()) {
            rules.push(ruleBuilder.createRule());
        }
        // console.log("player\n", rules);
        return [...rules];
    }

    positions({incomingValues = {}}) {
        const {
            sportConfig,
            playersData: {preparedPlayers},
        } = Action.getState();
        const players = preparedPlayers.groupedBy.allowedPositions();
        const {unitIdPropName = 'playerId'} = incomingValues;

        const rules = [];

        const positions = sportConfig.getConfigProp('positions');
        Object.keys(positions).map((positionNumber) => {
            const simplexUnitNamesByTeams = {};
            const addedPlayers = [];
            const posObj = positions[positionNumber];

            players.map((player) => {
                if (player.position != positionNumber || inArray(player[unitIdPropName], addedPlayers)) {
                    return;
                }

                if (!simplexUnitNamesByTeams.hasOwnProperty(player.team)) {
                    simplexUnitNamesByTeams[player.team] = {};
                }

                simplexUnitNamesByTeams[player.team][getSimplexUnitName(player[unitIdPropName])] = 1;

                addedPlayers.push(player[unitIdPropName]);
            });

            if (isEmpty(simplexUnitNamesByTeams)) {
                return;
            }

            let row = {};
            Object.values(simplexUnitNamesByTeams).map((simplexUnitNames) => {
                if (posObj.maxFromOneTeam) {
                    const ruleBuilder = RuleBuilder.fromAtMost(posObj.maxFromOneTeam);
                    ruleBuilder.addVars({...row});
                    rules.push(ruleBuilder.createRule());
                } else {
                    row = {...row, ...simplexUnitNames};
                }
            });

            if (isEmpty(row)) {
                return;
            }

            const {maxPlayers, minPlayers} = posObj;

            if (maxPlayers == minPlayers) {
                const ruleBuilder = RuleBuilder.fromExactly(maxPlayers);
                ruleBuilder.addVars({...row});

                rules.push(ruleBuilder.createRule());
            } else {
                if (Boolean(minPlayers)) {
                    const ruleBuilder = RuleBuilder.fromAtLeast(minPlayers);
                    ruleBuilder.addVars({...row});

                    rules.push(ruleBuilder.createRule());
                }
                if (Boolean(maxPlayers)) {
                    const ruleBuilder = RuleBuilder.fromAtMost(maxPlayers);
                    ruleBuilder.addVars({...row});

                    rules.push(ruleBuilder.createRule());
                }
            }
        });
        // console.log("positions\n",rules);
        return [...rules];
    }

    keyPlayerStacking({incomingValues: {
        checkIfPlayerOnDoublePosition = false,
        isOverlapPrimaryAndSecondaryStacksAllowed = false,
        modes = [],
    }}) {
        const {
            stacks: {groups: groupsOfStacks},
            playersData: {preparedPlayers},
            lineupGenerator: {settings},
            projectionPointsType,
            sportConfig,
        } = Action.getState();

        if (settings.isRelaxBuildRulesUsed() || !groupsOfStacks.hasItems()) {
            return [];
        }

        const getPlayerPoints = function (player) {
            return NumberHelper.float(player[projectionPointsType.getProjectionPointsType()], 3);
        };
        const calcStackBoostValue = function (stackItem, playerPoints) {
            return NumberHelper.float((stackItem.stackBoostPercent / 100) * avg(playerPoints), 2);
        };

        const playersIndexedById = preparedPlayers.groupedBy.playerId();
        const ConstraintsBuilder = KeyPlayerStackingConstraintsBuilderFactory.getBuilder();
        const constraintsBuilder = new ConstraintsBuilder({
            checkIfPlayerOnDoublePosition,
            players: playersIndexedById,
        });

        const constraints = [];
        const objective = {};

        const forcibleSimplexGroupUnitIds = [];
        const overlappingGroupUnitIdsMap = {};
        groupsOfStacks.all().map((stackItem) => {
            if (isEmpty(stackItem.players)) {
                return;
            }

            let playersRow = {};
            const playerPoints = [];
            stackItem.players.map((playerId) => {
                if (playerId in playersIndexedById) {
                    playersRow = {...playersRow, ...constraintsBuilder.createRow(playerId, 1, modes)};
                    playerPoints.push(getPlayerPoints(playersIndexedById[playerId]));
                }
            });

            if (isEmpty(playersRow)) {
                return;
            }

            const [keyPlayerId] = stackItem.keys;

            if (!(keyPlayerId in playersIndexedById)) {
                return;
            }

            const keyPlayer = playersIndexedById[keyPlayerId];

            playerPoints.push(getPlayerPoints(keyPlayer));

            const simplexGroupUnitId = KeyPlayerStackMarker.setSuffix(
                getSimplexKeyUnitName([stackItem.stackItemId, keyPlayerId]),
            );

            if (inArray(stackItem.condition, [Const.STACKS_TYPE_EXACTLY, Const.STACKS_TYPE_AT_LEAST])) {
                if (Object.keys(playersRow).length < stackItem.limit) {
                    return;
                }

                const builder = RuleBuilder.fromAtLeast(0);
                builder.addVar(simplexGroupUnitId, -stackItem.limit);
                builder.addVars(playersRow);

                constraints.push(builder.createRule());
            }

            if (inArray(stackItem.condition, [Const.STACKS_TYPE_EXACTLY, Const.STACKS_TYPE_AT_MOST])) {
                const playersQuantity = stackItem.players.length;
                const builder = RuleBuilder.fromAtMost(playersQuantity);
                builder.addVar(simplexGroupUnitId, playersQuantity - stackItem.limit);
                builder.addVars(playersRow);

                constraints.push(builder.createRule());
            }

            overlappingGroupUnitIdsMap[keyPlayer.team] ??= {
                opposingTeamName: keyPlayer.opponent,
                primary: [],
                secondary: [],
            };

            if (stackItem.isPrimaryStack) {
                const builder = RuleBuilder.fromExactly(0);
                builder.addVar(simplexGroupUnitId, 1);
                builder.addVars(
                    constraintsBuilder.createRow(
                        keyPlayerId,
                        -1,
                        sportConfig.isGameTypeShowDownOrSingleGame() ? [stackItem.keyPlayerLineupPosition] : [],
                    ),
                );

                constraints.push(builder.createRule());

                overlappingGroupUnitIdsMap[keyPlayer.team].primary.push({
                    simplexGroupUnitId,
                    isSameTeam: stackItem.isSameTeam,
                });
            } else {
                const builder = RuleBuilder.fromAtLeast(0);
                builder.addVar(simplexGroupUnitId, -1);
                builder.addVars(constraintsBuilder.createRow(keyPlayerId, 1));

                constraints.push(builder.createRule());

                overlappingGroupUnitIdsMap[keyPlayer.team].secondary.push({
                    simplexGroupUnitId,
                    isSameTeam: stackItem.isSameTeam,
                });

                stackItem.isStackItemForcible && forcibleSimplexGroupUnitIds.push(simplexGroupUnitId);
            }

            objective[simplexGroupUnitId] = calcStackBoostValue(stackItem, playerPoints);
        });

        if (!isEmpty(forcibleSimplexGroupUnitIds)) {
            const builder = RuleBuilder.fromAtLeast(1);
            forcibleSimplexGroupUnitIds.map((simplexGroupUnitId) => builder.addVar(simplexGroupUnitId, 1));
            constraints.push(builder.createRule());
        }

        if (!isOverlapPrimaryAndSecondaryStacksAllowed) {
            Object.keys(overlappingGroupUnitIdsMap).map((teamName) => {
                const {opposingTeamName, primary, secondary} = overlappingGroupUnitIdsMap[teamName];

                primary.map((item) => {
                    const overlappingSimplexGroupUnitIds = secondary.map((item) => item.simplexGroupUnitId);

                    if (item.isSameTeam) {
                        (overlappingGroupUnitIdsMap[opposingTeamName]?.secondary ?? [])
                            .filter((item) => !item.isSameTeam)
                            .forEach((item) => overlappingSimplexGroupUnitIds.push(item.simplexGroupUnitId));
                    } else {
                        (overlappingGroupUnitIdsMap[opposingTeamName]?.secondary ?? [])
                            .forEach((item) => overlappingSimplexGroupUnitIds.push(item.simplexGroupUnitId));
                    }

                    if (!isEmpty(overlappingSimplexGroupUnitIds)) {
                        const builder = RuleBuilder.fromAtMost(1);
                        builder.addVar(item.simplexGroupUnitId, 1);
                        overlappingSimplexGroupUnitIds.map((simplexGroupUnitId) => builder.addVar(simplexGroupUnitId, 1));
                        constraints.push(builder.createRule());
                    }
                });
            });
        }

        this.store.patchObj('additionalObjective', {
            keyPlayerStacking: {...objective},
        });

        return [...constraints];
    }

    keyPlayerStackingComboExpMax({incomingValues: {checkIfPlayerOnDoublePosition = false}}) {
        const {
            stacks: {groups: groupsOfStacks},
            runSettings,
            keyPlayerStacking: {
                combinations: {exposureMax: kpcExpMax},
            },
            lineupsData: {rawLineups},
            playersData: {preparedPlayers, players},
            lineupGenerator: {settings},
        } = Action.getState();

        if (settings.isRelaxBuildRulesUsed() || !groupsOfStacks.hasItems()) {
            return [];
        }

        const ConstraintsBuilder = KeyPlayerStackingConstraintsBuilderFactory.getBuilder();
        const constraintsBuilder = new ConstraintsBuilder({
            checkIfPlayerOnDoublePosition,
            players: preparedPlayers.groupedBy.playerId(),
        });

        function getPrimaryCombinationsState() {
            const expMaxCombinations = {};
            groupsOfStacks.all().map((item) => {
                const [keyPlayerId] = item.keys;
                const combinations = kpcExpMax.getStackCombos(keyPlayerId);

                if (isEmpty(combinations)) {
                    return;
                }

                if (!expMaxCombinations.hasOwnProperty(keyPlayerId)) {
                    expMaxCombinations[keyPlayerId] = {};
                }

                Object.values(combinations).map((combo) => {
                    expMaxCombinations[keyPlayerId][combo.getId()] = {
                        maxQuantity: combo.getExpMax(),
                        stackedPlayerIds: combo.getCombo(),
                    };
                });
            });

            return expMaxCombinations;
        }

        const getExpMaxCombinationsState = function () {
            let expMaxCombinations = this.store.get('kps.expMaxCombinations', {});

            if (isEmpty(expMaxCombinations)) {
                expMaxCombinations = getPrimaryCombinationsState();
            }

            const lastLineup = rawLineups.getLast();
            if (!isEmpty(lastLineup)) {
                const analyzer = new KeyPlayerStackAnalyzer({
                    players,
                    stacking: runSettings.getRunSettingsByKey(groupsOfStacks.getStateId()),
                    simplexLineup: lastLineup,
                });
                analyzer.getKeyPlayerIds().map((playerId) => {
                    const comboId = kpcExpMax.doComboId(analyzer.getStackedPlayerIdsByKeyPlayerId(playerId));
                    if (
                        expMaxCombinations.hasOwnProperty(playerId) &&
                        expMaxCombinations[playerId].hasOwnProperty(comboId)
                    ) {
                        expMaxCombinations[playerId][comboId].maxQuantity--;
                    }
                });
            }

            this.store.set('kps.expMaxCombinations', expMaxCombinations);

            return expMaxCombinations;
        }.bind(this);

        const expMaxCombinationsState = getExpMaxCombinationsState();
        const constraints = constraintsBuilder.createConstraintForExpMaxCombinations({
            combinationsState: expMaxCombinationsState,
        });
        // console.log("keyPlayerStackingComboExpMaxConstraints\n",constraints);
        return [...constraints];
    }

    teamNameStacking({incomingValues: {checkIfPlayerOnDoublePosition = false}}) {
        const {
            stacks: {
                teamNameStackingGroups: {data: groupsOfStacks},
            },
            playersData: {preparedPlayers},
            lineupGenerator: {settings},
        } = Action.getState();

        if (settings.isRelaxBuildRulesUsed() || !groupsOfStacks.length) {
            return [];
        }

        const players = preparedPlayers.groupedBy.playerId();

        function isPlayerOnSinglePosition(playerId) {
            if (!checkIfPlayerOnDoublePosition) {
                return true;
            }
            return !(players.hasOwnProperty(playerId) && isPlayerOnDoublePosition(players[playerId]));
        }

        function createRow(playerId, incomingFactor) {
            const factor = incomingFactor || 1;

            const row = {};
            if (isPlayerOnSinglePosition(playerId)) {
                row[getSimplexUnitName(playerId)] = factor;
            } else {
                players[playerId].position.map((pos) => {
                    row[getSimplexUnitName([playerId, pos])] = factor;
                });
            }

            return row;
        }

        const resArr = [];
        groupsOfStacks.map((group) => {
            let players = {};
            group.players.map((playerId) => {
                players = {...players, ...createRow(playerId)};
            });

            resArr.push({
                row: {...players},
                type: getStackTypeNumberByType(group.condition),
                obj: Number(group.limit),
            });
        });

        let result = [...resArr];
        // console.log("createTeamNameStackingRule\n",result);
        return result;
    }

    correlationStackingPrimaryRule({incomingValues: {checkIfPlayerOnDoublePosition = false}}) {
        const {
            correlationStacking: {
                settings: csSettings,
                combinations: {exposureMax: cscExpMax},
            },
            playersData: {preparedPlayers, players},
            lineupsData: {rawLineups},
            runSettings,
            sportConfig,
            lineupGenerator: {settings},
        } = Action.getState();
        const playersGroupedById = preparedPlayers.groupedBy.playerId();
        const forbiddenPlayers = this.store.get('playersForbiddenByExposureMax', []);
        // console.log('forbiddenPlayers',{...forbiddenPlayers});

        let rules = [];

        if (settings.isRelaxBuildRulesUsed() || !csSettings.isEnabled()) {
            return rules;
        }
        // console.log('csSettings',{...csSettings});
        // console.log('cscExpMax',{...cscExpMax});
        function isPlayerOnSinglePosition(playerId) {
            if (!checkIfPlayerOnDoublePosition) {
                return true;
            }
            return !(
                playersGroupedById.hasOwnProperty(playerId) && isPlayerOnDoublePosition(playersGroupedById[playerId])
            );
        }

        function getSimplexUnitNameByPlayerIdAndPositionName(playerId, positionName) {
            const position = sportConfig.getPositionByName(positionName);

            if (isEmpty(position)) {
                return getSimplexUnitName(playerId);
            }

            const [positionKey] = position.allowedPositions;

            if (!positionKey) {
                return getSimplexUnitName(playerId);
            }

            return getSimplexUnitName([playerId, positionKey]);
        }

        function getSimplexUnitNamesByStackPlayer(stackPlayer) {
            if (
                !playersGroupedById.hasOwnProperty(stackPlayer.getId()) ||
                isPlayerOnSinglePosition(stackPlayer.getId())
            ) {
                return [getSimplexUnitName(stackPlayer.getId())];
            }

            const player = playersGroupedById[stackPlayer.getId()];
            const positionNames = arrayDuplicates(
                player.positionName.split('/'),
                stackPlayer.getStackablePlayerPositionNames(),
            );

            return positionNames.map((positionName) => {
                return getSimplexUnitNameByPlayerIdAndPositionName(stackPlayer.getId(), positionName);
            });
        }

        const getStackCorrelationState = function () {
            const prState = this.store.get('cs.pr.state', {});

            if (isEmpty(prState)) {
                csSettings.getItems().forEach((item) => {
                    const stackLineupsQuantity = item.getMinExposure();

                    const players = {};
                    item.getStackPlayers().forEach((stackPlayer) => {
                        const maxQuantity = calcAndRoundDownValByPct(
                            stackLineupsQuantity,
                            stackPlayer.getMaxExposure(),
                        );

                        players[stackPlayer.getId()] = {maxQuantity};
                    });

                    prState[item.getId()] = {
                        minQuantity: stackLineupsQuantity,
                        players,
                    };
                });
            }

            const lastLineup = rawLineups.getLast();

            if (!isEmpty(lastLineup)) {
                const stackItemIds = lastLineup.getDistilled(Const.LP_CS_PRIMARY_STACK_GROUP_PREFIX);
                if (!isEmpty(stackItemIds)) {
                    const [stackItemId] = stackItemIds;
                    if (prState.hasOwnProperty(stackItemId)) {
                        const playersIds = lastLineup.getPlayerIds();
                        prState[stackItemId].minQuantity--;
                        Object.keys(prState[stackItemId].players).map((pId) => {
                            if (inArray(pId, playersIds)) {
                                prState[stackItemId].players[pId].maxQuantity--;
                            }
                        });
                    }
                }
            }

            this.store.set('cs.pr.state', prState);

            return prState;
        }.bind(this);

        const getExpMaxCombinationsState = function () {
            const expMaxCombinations = this.store.get('cs.pr.expMaxCombinations', {});

            if (isEmpty(expMaxCombinations)) {
                csSettings.getItems().forEach((item) => {
                    const combinations = cscExpMax.getStackCombos(item.getId());

                    if (isEmpty(combinations)) {
                        return;
                    }

                    if (!expMaxCombinations.hasOwnProperty(item.getId())) {
                        expMaxCombinations[item.getId()] = {};
                    }

                    Object.values(combinations).map((combo) => {
                        expMaxCombinations[item.getId()][combo.getId()] = {
                            maxQuantity: calcAndRoundDownValByPct(item.getMinExposure(), combo.getExpMax()),
                            stackedPlayerIds: combo.getCombo(),
                        };
                    });
                });
            }

            const lastLineup = rawLineups.getLast();
            if (!isEmpty(lastLineup)) {
                const [stackItemId] = lastLineup.getDistilled(Const.LP_CS_PRIMARY_STACK_GROUP_PREFIX);
                const stackItem = csSettings.getItem(stackItemId);
                if (stackItem) {
                    const keyPlayer = players.getPlayer(stackItem.getKeyPlayerId());
                    const analyzer = new CorrelationStackLineupAnalyzer(players);
                    analyzer.analyze(lastLineup, {
                        teamName: keyPlayer.team,
                        isGameStack: stackItem.isGameStackType(),
                        onlyPositions: Const.CORRELATION_STACKING_STACKABLE_PLAYER_POSITION_NAMES,
                    });
                    const comboId = cscExpMax.doComboId(analyzer.getStackedPlayerIds());
                    if (
                        expMaxCombinations.hasOwnProperty(stackItemId) &&
                        expMaxCombinations[stackItemId].hasOwnProperty(comboId)
                    ) {
                        expMaxCombinations[stackItemId][comboId].maxQuantity--;
                    }
                }
            }

            this.store.set('cs.pr.expMaxCombinations', expMaxCombinations);

            return expMaxCombinations;
        }.bind(this);

        function defineNeededStackingItem(stackCorrelationState, stackingItems) {
            if (isEmpty(stackingItems)) {
                return null;
            }

            for (let key in stackCorrelationState) {
                const item = stackingItems.getItem(key);
                if (0 < stackCorrelationState[key].minQuantity && !inArray(item.getKeyPlayerId(), forbiddenPlayers)) {
                    return item;
                }
            }

            return null;
        }

        function defineStackPlayersList(stackPlayersState, stackPlayers) {
            const result = [];

            stackPlayers.forEach((item) => {
                if (!playersGroupedById.hasOwnProperty(item.getId())) {
                    return;
                }
                if (inArray(item.getId(), forbiddenPlayers)) {
                    return;
                }
                if (0 >= stackPlayersState[item.getId()].maxQuantity) {
                    return;
                }

                result.push(item);
            });

            return result;
        }

        function defineForbiddenPlayersList(stackPlayersState, stackPlayers) {
            const result = [];

            stackPlayers.forEach((item) => {
                if (!playersGroupedById.hasOwnProperty(item.getId())) {
                    return;
                }
                if (inArray(item.getId(), forbiddenPlayers)) {
                    return;
                }
                if (0 < stackPlayersState[item.getId()].maxQuantity) {
                    return;
                }

                result.push(item);
            });

            return result;
        }

        function getAtLeastRule(stackItemId, stackPlayers, atLeast) {
            const builder = RuleBuilder.fromAtLeast(0);
            builder.addVar(Const.LP_CS_PRIMARY_STACK_GROUP_PREFIX + stackItemId, -1 * atLeast);
            stackPlayers.map((item) =>
                getSimplexUnitNamesByStackPlayer(item).map((simplexUnitName) => {
                    builder.addVar(simplexUnitName, 1);
                }),
            );

            return builder.createRule();
        }

        function getAtMostRule(stackItemId, stackPlayers, atMost) {
            const builder = RuleBuilder.fromAtMost(1 + atMost);
            // builder.atMost(stackPlayers.length);
            // builder.addVar(Const.LP_CS_PRIMARY_STACK_GROUP_PREFIX + stackItemId, (stackPlayers.length - atMost));
            builder.addVar(Const.LP_CS_PRIMARY_STACK_GROUP_PREFIX + stackItemId, 1);
            stackPlayers.map((item) =>
                getSimplexUnitNamesByStackPlayer(item).map((simplexUnitName) => {
                    builder.addVar(simplexUnitName, 1);
                }),
            );

            return builder.createRule();
        }

        function getStackItemRule(stackItem) {
            const ruleBuilder = RuleBuilder.fromExactly(1);
            ruleBuilder.addVar(Const.LP_CS_PRIMARY_STACK_GROUP_PREFIX + stackItem.getId(), 1);

            return ruleBuilder.createRule();
        }

        function getForbiddenPlayersRule(stackPlayers) {
            const builder = RuleBuilder.fromExactly(0);
            stackPlayers.map((item) =>
                getSimplexUnitNamesByStackPlayer(item).map((simplexUnitName) => {
                    builder.addVar(simplexUnitName, 1);
                }),
            );

            return builder.createRule();
        }

        function getObjectiveRuleOfStackPlayers(stackPlayers) {
            const result = {};
            stackPlayers.map((item) => {
                const points = NumberHelper.float(item.getPoints(), 1);
                getSimplexUnitNamesByStackPlayer(item).map((simplexUnitName) => {
                    result[simplexUnitName] = points;
                });
            });

            return result;
        }

        function getRuleForStackedPlayers(stackItemId, combinationsState) {
            const rules = [];

            if (!combinationsState.hasOwnProperty(stackItemId)) {
                return rules;
            }

            const comboState = combinationsState[stackItemId];
            Object.values(comboState).map((state) => {
                if (0 >= state.maxQuantity && !isEmpty(state.stackedPlayerIds)) {
                    const builder = RuleBuilder.fromAtMost(state.stackedPlayerIds.length - 1);
                    state.stackedPlayerIds.map((id) => {
                        const simplexUnitNames = getSimplexUnitNamesByStackPlayer({
                            getId: () => id,
                            getStackablePlayerPositionNames: () =>
                                Const.CORRELATION_STACKING_STACKABLE_PLAYER_POSITION_NAMES,
                        });
                        simplexUnitNames.map((simplexUnitName) => builder.addVar(simplexUnitName, 1));
                    });

                    rules.push(builder.createRule());
                }
            });

            return rules;
        }

        function createCombosMap(keyPlayer, stackItemId, firstTeamPlayerIds, secondTeamPlayerIds) {
            return {
                [keyPlayer.team + keyPlayer.opponent]: {
                    [keyPlayer.team]: [
                        {
                            keyUnitId: Const.LP_CS_PRIMARY_STACK_GROUP_PREFIX + stackItemId,
                            playerIds: firstTeamPlayerIds,
                        },
                    ],
                    [keyPlayer.opponent]: [
                        {
                            keyUnitId: Const.LP_CS_PRIMARY_STACK_GROUP_PREFIX + stackItemId,
                            playerIds: secondTeamPlayerIds,
                        },
                    ],
                },
            };
        }

        const stackCorrelationState = getStackCorrelationState();
        // console.log('stackCorrelationState',{...stackCorrelationState});
        // 1. Define list of necessary stacking items that can be taken for lineup.
        // List of items is limited by exposure max of key player.
        const stackItem = defineNeededStackingItem(stackCorrelationState, csSettings);
        // console.log('stackItem',{...stackItem});
        this.store.set('cs.currentItem', stackItem);

        if (isEmpty(stackItem)) {
            this.store.patchObj('additionalObjective', {
                correlationStackingPrimaryRule: {},
            });
            return rules;
        }

        // 2. For each of taken key player define stacking players.
        // List of players is limited by exposure max of stacking players.
        // Define rules "at least" and "at most" for both categories of stack.
        let objectiveRule = {};

        const firstTeamStackingPlayers = defineStackPlayersList(
            stackCorrelationState[stackItem.getId()].players,
            stackItem.getFirstTeamStackPlayers(),
        );

        if (!isEmpty(firstTeamStackingPlayers)) {
            const firstTeamAtLeastRule = getAtLeastRule(
                stackItem.getId(),
                firstTeamStackingPlayers,
                stackItem.getFirstTeamAtLeastPlayers(),
            );
            rules.push(firstTeamAtLeastRule);

            const firstTeamAtMostRule = getAtMostRule(
                stackItem.getId(),
                firstTeamStackingPlayers,
                stackItem.getFirstTeamAtMostPlayers(),
            );
            rules.push(firstTeamAtMostRule);

            objectiveRule = {
                ...objectiveRule,
                ...getObjectiveRuleOfStackPlayers(firstTeamStackingPlayers),
            };
        }

        const secondTeamStackingPlayers = defineStackPlayersList(
            stackCorrelationState[stackItem.getId()].players,
            stackItem.getSecondTeamStackPlayers(),
        );
        // console.log('secondTeamStackingPlayers',{...secondTeamStackingPlayers});
        if (!isEmpty(secondTeamStackingPlayers)) {
            const secondTeamAtLeastRule = getAtLeastRule(
                stackItem.getId(),
                secondTeamStackingPlayers,
                stackItem.getSecondTeamAtLeastPlayers(),
            );
            rules.push(secondTeamAtLeastRule);

            const secondTeamAtMostRule = getAtMostRule(
                stackItem.getId(),
                secondTeamStackingPlayers,
                stackItem.getSecondTeamAtMostPlayers(),
            );
            rules.push(secondTeamAtMostRule);

            objectiveRule = {
                ...objectiveRule,
                ...getObjectiveRuleOfStackPlayers(secondTeamStackingPlayers),
            };
        }

        if (!isEmpty(rules)) {
            const combosMap = createCombosMap(
                playersGroupedById[stackItem.getKeyPlayerId()],
                stackItem.getId(),
                [...getPropValues(firstTeamStackingPlayers, 'id'), stackItem.getKeyPlayerId()],
                getPropValues(secondTeamStackingPlayers, 'id'),
            );
            this.store.set('cs.pr.combosMap', combosMap);

            const keyPlayerAtLeastRule = getAtLeastRule(
                stackItem.getId(),
                [
                    {
                        getId: () => stackItem.getKeyPlayerId(),
                        getStackablePlayerPositionNames: () => ['QB'],
                    },
                ],
                1,
            );

            rules.push(keyPlayerAtLeastRule);
            rules.push(getStackItemRule(stackItem));

            const forbiddenStackingPlayers = defineForbiddenPlayersList(
                stackCorrelationState[stackItem.getId()].players,
                stackItem.getStackPlayers(),
            );
            if (!isEmpty(forbiddenStackingPlayers)) {
                rules.push(getForbiddenPlayersRule(forbiddenStackingPlayers));
            }

            const expMaxCombinationsState = getExpMaxCombinationsState();

            const ruleForStackedPlayers = getRuleForStackedPlayers(stackItem.getId(), expMaxCombinationsState);
            rules = [...rules, ...ruleForStackedPlayers];

            objectiveRule[Const.LP_CS_PRIMARY_STACK_GROUP_PREFIX + stackItem.getId()] = 0;
        }

        this.store.patchObj('additionalObjective', {
            correlationStackingPrimaryRule: {...objectiveRule},
        });
        // console.log("correlationStackingPrimaryRule\n", [...rules]);
        return [...rules];
    }

    correlationStackingSecondaryRule({incomingValues: {checkIfPlayerOnDoublePosition = false}}) {
        const {
            playersData: {players: playersList, preparedPlayers},
            sportConfig,
            lineupGenerator: {settings},
        } = Action.getState();

        const players = preparedPlayers.groupedBy.playerId();
        const stackItem = this.store.get('cs.currentItem');

        let resArr = [];

        if (settings.isRelaxBuildRulesUsed() || !stackItem || stackItem.isSecondaryStackTypeNone()) {
            return resArr;
        }

        const keyPlayer = players[stackItem.getKeyPlayerId()];

        const forbiddenPlayers = this.store.get('playersForbiddenByExposureMax', []);

        function isPlayerOnSinglePosition(playerId) {
            if (!checkIfPlayerOnDoublePosition) {
                return true;
            }
            return !(players.hasOwnProperty(playerId) && isPlayerOnDoublePosition(players[playerId]));
        }

        function getSimplexUnitNameByPlayerIdAndPositionName(playerId, positionName) {
            if (isPlayerOnSinglePosition(playerId)) {
                return getSimplexUnitName(playerId);
            }

            const position = sportConfig.getPositionByName(positionName);

            if (isEmpty(position)) {
                return getSimplexUnitName(playerId);
            }

            const [positionKey] = position.allowedPositions;

            if (!positionKey) {
                return getSimplexUnitName(playerId);
            }

            return getSimplexUnitName([playerId, positionKey]);
        }

        function formPlayerGroupsByPositions(playersGroupedByPosition, positionNames) {
            const playerGroups = [];
            positionNames.map((positionName) => {
                const playerIds = extractPlayerIds(playersGroupedByPosition, [positionName]);
                if (!isEmpty(playerIds)) {
                    playerGroups.push({positionName, playerIds});
                }
            });

            return playerGroups;
        }

        function getAtLeastRule(keyUnitId, playerGroups, minStakingUnitsQuantity = -1) {
            const builder = RuleBuilder.fromAtLeast(0);
            builder.addVar(keyUnitId, minStakingUnitsQuantity);
            playerGroups.map((item) =>
                item.playerIds.map((playerId) =>
                    builder.addVar(getSimplexUnitNameByPlayerIdAndPositionName(playerId, item.positionName), 1),
                ),
            );

            return builder.createRule();
        }

        function extractPlayerIds(playersByPositions, positions) {
            const result = [];
            Object.keys(playersByPositions).map((positionName) => {
                const duplicates = arrayDuplicates(positionName.split('/'), positions);
                if (isEmpty(duplicates)) {
                    return;
                }

                playersByPositions[positionName].map((p) => {
                    if (!inArray(p.getId(), forbiddenPlayers)) {
                        result.push(p.getId());
                    }
                });
            });

            return result;
        }

        const rules = this.store.get('cs.sr.rules', {});
        const srCombosMap = this.store.get('cs.sr.combosMap', {});

        if (!rules.hasOwnProperty(stackItem.getId())) {
            const combinations = stackItem.getSecondaryStackCombinations();
            const map = preparedPlayers.groupedBy.gameTeamPosName();
            const stackItemRules = {};
            const combosMap = {};
            // console.log('map',{...map});
            Object.keys(map).map((gameId) => {
                // Take teams that do not play in KeyG.
                if (keyPlayer.getGameKey() === gameId) {
                    return;
                }

                const [firstTeam, secondTeam] = Object.values(map[gameId]);

                combosMap[gameId] = {
                    [firstTeam.name]: {},
                    [secondTeam.name]: {},
                };
                // According to the secondary stack combinations create rules for each group of players by game.
                Object.keys(combinations).map((type) => {
                    if (!combinations[type]) {
                        return;
                    }

                    const {mainTeamPositions, opponentTeamPositions} = Const.SECONDARY_STACK_COMBINATION_TYPES[type];
                    const typeNumber = Number(type);

                    if (typeNumber === Const.SECONDARY_STACK_COMBINATION_TYPE_1) {
                        {
                            const ftPlayerGroups = formPlayerGroupsByPositions(firstTeam.positions, mainTeamPositions);
                            const stPlayerGroups = formPlayerGroupsByPositions(
                                secondTeam.positions,
                                opponentTeamPositions,
                            );

                            if (!isEmpty(ftPlayerGroups) && !isEmpty(stPlayerGroups)) {
                                const keyUnitId = getCSComboVarName([stackItem.getId(), firstTeam.name, 1]);

                                let playerIds = [];
                                ftPlayerGroups.map((item) => (playerIds = [...playerIds, ...item.playerIds]));
                                stPlayerGroups.map((item) => (playerIds = [...playerIds, ...item.playerIds]));

                                combosMap[gameId][firstTeam.name][1] = {keyUnitId, playerIds};

                                stackItemRules[keyUnitId] = [
                                    getAtLeastRule(keyUnitId, ftPlayerGroups),
                                    getAtLeastRule(keyUnitId, stPlayerGroups),
                                ];
                            }
                        }
                        {
                            const ftPlayerGroups = formPlayerGroupsByPositions(
                                firstTeam.positions,
                                opponentTeamPositions,
                            );
                            const stPlayerGroups = formPlayerGroupsByPositions(secondTeam.positions, mainTeamPositions);

                            if (!isEmpty(ftPlayerGroups) && !isEmpty(stPlayerGroups)) {
                                const keyUnitId = getCSComboVarName([stackItem.getId(), secondTeam.name, 1]);

                                let playerIds = [];
                                ftPlayerGroups.map((item) => (playerIds = [...playerIds, ...item.playerIds]));
                                stPlayerGroups.map((item) => (playerIds = [...playerIds, ...item.playerIds]));

                                combosMap[gameId][secondTeam.name][1] = {keyUnitId, playerIds};

                                stackItemRules[keyUnitId] = [
                                    getAtLeastRule(keyUnitId, ftPlayerGroups),
                                    getAtLeastRule(keyUnitId, stPlayerGroups),
                                ];
                            }
                        }
                    } else if (typeNumber === Const.SECONDARY_STACK_COMBINATION_TYPE_2) {
                        {
                            const ftPlayerGroups = formPlayerGroupsByPositions(firstTeam.positions, mainTeamPositions);
                            const stPlayerGroups = formPlayerGroupsByPositions(
                                secondTeam.positions,
                                opponentTeamPositions,
                            );

                            if (!isEmpty(ftPlayerGroups) && !isEmpty(stPlayerGroups)) {
                                const keyUnitId = getCSComboVarName([stackItem.getId(), firstTeam.name, 2]);

                                let playerIds = [];
                                ftPlayerGroups.map((item) => (playerIds = [...playerIds, ...item.playerIds]));
                                stPlayerGroups.map((item) => (playerIds = [...playerIds, ...item.playerIds]));

                                combosMap[gameId][firstTeam.name][2] = {keyUnitId, playerIds};

                                stackItemRules[keyUnitId] = [
                                    getAtLeastRule(keyUnitId, ftPlayerGroups),
                                    getAtLeastRule(keyUnitId, stPlayerGroups),
                                ];
                            }
                        }
                        {
                            const ftPlayerGroups = formPlayerGroupsByPositions(
                                firstTeam.positions,
                                opponentTeamPositions,
                            );
                            const stPlayerGroups = formPlayerGroupsByPositions(secondTeam.positions, mainTeamPositions);

                            if (!isEmpty(ftPlayerGroups) && !isEmpty(stPlayerGroups)) {
                                const keyUnitId = getCSComboVarName([stackItem.getId(), secondTeam.name, 2]);

                                let playerIds = [];
                                ftPlayerGroups.map((item) => (playerIds = [...playerIds, ...item.playerIds]));
                                stPlayerGroups.map((item) => (playerIds = [...playerIds, ...item.playerIds]));

                                combosMap[gameId][secondTeam.name][2] = {keyUnitId, playerIds};

                                stackItemRules[keyUnitId] = [
                                    getAtLeastRule(keyUnitId, ftPlayerGroups),
                                    getAtLeastRule(keyUnitId, stPlayerGroups),
                                ];
                            }
                        }
                    } else if (typeNumber === Const.SECONDARY_STACK_COMBINATION_TYPE_3) {
                        {
                            const [firstPosition, secondPosition] = mainTeamPositions;
                            let fpPlayerGroups = [],
                                spPlayerGroups = [];
                            if (firstPosition) {
                                fpPlayerGroups = formPlayerGroupsByPositions(firstTeam.positions, [firstPosition]);
                            }
                            if (secondPosition) {
                                spPlayerGroups = formPlayerGroupsByPositions(firstTeam.positions, [secondPosition]);
                            }

                            if (!isEmpty(fpPlayerGroups) && !isEmpty(spPlayerGroups)) {
                                const keyUnitId = getCSComboVarName([stackItem.getId(), firstTeam.name, 3]);

                                let playerIds = [];
                                fpPlayerGroups.map((item) => (playerIds = [...playerIds, ...item.playerIds]));
                                spPlayerGroups.map((item) => (playerIds = [...playerIds, ...item.playerIds]));

                                combosMap[gameId][firstTeam.name][3] = {keyUnitId, playerIds};

                                stackItemRules[keyUnitId] = [
                                    getAtLeastRule(keyUnitId, fpPlayerGroups),
                                    getAtLeastRule(keyUnitId, spPlayerGroups),
                                ];
                            }
                        }
                        {
                            const [firstPosition, secondPosition] = mainTeamPositions;
                            let fpPlayerGroups = [],
                                spPlayerGroups = [];
                            if (firstPosition) {
                                fpPlayerGroups = formPlayerGroupsByPositions(secondTeam.positions, [firstPosition]);
                            }
                            if (secondPosition) {
                                spPlayerGroups = formPlayerGroupsByPositions(secondTeam.positions, [secondPosition]);
                            }

                            if (!isEmpty(fpPlayerGroups) && !isEmpty(spPlayerGroups)) {
                                const keyUnitId = getCSComboVarName([stackItem.getId(), secondTeam.name, 3]);

                                let playerIds = [];
                                fpPlayerGroups.map((item) => (playerIds = [...playerIds, ...item.playerIds]));
                                spPlayerGroups.map((item) => (playerIds = [...playerIds, ...item.playerIds]));

                                combosMap[gameId][secondTeam.name][3] = {keyUnitId, playerIds};

                                stackItemRules[keyUnitId] = [
                                    getAtLeastRule(keyUnitId, fpPlayerGroups),
                                    getAtLeastRule(keyUnitId, spPlayerGroups),
                                ];
                            }
                        }
                    } else if (typeNumber === Const.SECONDARY_STACK_COMBINATION_TYPE_4) {
                        {
                            const [firstPosition, ...secondPositions] = mainTeamPositions;
                            let fpPlayerGroups = [],
                                spPlayerGroups = [];
                            if (firstPosition) {
                                fpPlayerGroups = formPlayerGroupsByPositions(firstTeam.positions, [firstPosition]);
                            }
                            if (!isEmpty(secondPositions)) {
                                spPlayerGroups = formPlayerGroupsByPositions(firstTeam.positions, secondPositions);
                            }

                            if (!isEmpty(fpPlayerGroups) && !isEmpty(spPlayerGroups)) {
                                const keyUnitId = getCSComboVarName([stackItem.getId(), firstTeam.name, 4]);

                                let playerIds = [];
                                fpPlayerGroups.map((item) => (playerIds = [...playerIds, ...item.playerIds]));
                                spPlayerGroups.map((item) => (playerIds = [...playerIds, ...item.playerIds]));

                                combosMap[gameId][firstTeam.name][4] = {keyUnitId, playerIds};

                                stackItemRules[keyUnitId] = [
                                    getAtLeastRule(keyUnitId, fpPlayerGroups),
                                    getAtLeastRule(keyUnitId, spPlayerGroups),
                                ];
                            }
                        }
                        {
                            const [firstPosition, ...secondPositions] = mainTeamPositions;
                            let fpPlayerGroups = [],
                                spPlayerGroups = [];
                            if (firstPosition) {
                                fpPlayerGroups = formPlayerGroupsByPositions(secondTeam.positions, [firstPosition]);
                            }
                            if (!isEmpty(secondPositions)) {
                                spPlayerGroups = formPlayerGroupsByPositions(secondTeam.positions, secondPositions);
                            }

                            if (!isEmpty(fpPlayerGroups) && !isEmpty(spPlayerGroups)) {
                                const keyUnitId = getCSComboVarName([stackItem.getId(), secondTeam.name, 4]);

                                let playerIds = [];
                                fpPlayerGroups.map((item) => (playerIds = [...playerIds, ...item.playerIds]));
                                spPlayerGroups.map((item) => (playerIds = [...playerIds, ...item.playerIds]));

                                combosMap[gameId][secondTeam.name][4] = {keyUnitId, playerIds};

                                stackItemRules[keyUnitId] = [
                                    getAtLeastRule(keyUnitId, fpPlayerGroups),
                                    getAtLeastRule(keyUnitId, spPlayerGroups),
                                ];
                            }
                        }
                    } else if (typeNumber === Const.SECONDARY_STACK_COMBINATION_TYPE_5) {
                        {
                            const playerGroups = formPlayerGroupsByPositions(firstTeam.positions, mainTeamPositions);

                            if (!isEmpty(playerGroups)) {
                                const keyUnitId = getCSComboVarName([stackItem.getId(), firstTeam.name, 5]);

                                let playerIds = [];
                                playerGroups.map((item) => (playerIds = [...playerIds, ...item.playerIds]));

                                combosMap[gameId][firstTeam.name][5] = {keyUnitId, playerIds};

                                stackItemRules[keyUnitId] = [getAtLeastRule(keyUnitId, playerGroups, -2)];
                            }
                        }
                        {
                            const playerGroups = formPlayerGroupsByPositions(secondTeam.positions, mainTeamPositions);

                            if (!isEmpty(playerGroups)) {
                                const keyUnitId = getCSComboVarName([stackItem.getId(), secondTeam.name, 5]);

                                let playerIds = [];
                                playerGroups.map((item) => (playerIds = [...playerIds, ...item.playerIds]));

                                combosMap[gameId][secondTeam.name][5] = {keyUnitId, playerIds};

                                stackItemRules[keyUnitId] = [getAtLeastRule(keyUnitId, playerGroups, -2)];
                            }
                        }
                    }
                });
            });
            // console.log('stackItemRules',{...stackItemRules});
            rules[stackItem.getId()] = {};
            if (!isEmpty(stackItemRules)) {
                rules[stackItem.getId()] = stackItemRules;
            }
            srCombosMap[stackItem.getId()] = combosMap;

            this.store.set('cs.sr.rules', rules);
            this.store.set('cs.sr.combosMap', srCombosMap);
        }

        const secondaryStackRules = rules[stackItem.getId()];

        if (!isEmpty(secondaryStackRules)) {
            // console.log('secondaryStackRules',{...secondaryStackRules});
            const objectiveRule = {};
            const keyUnitsRow = {};
            Object.keys(secondaryStackRules).map((secondaryStackItemId) => {
                objectiveRule[secondaryStackItemId] = 0;
                keyUnitsRow[secondaryStackItemId] = 1;
                resArr = [...resArr, ...secondaryStackRules[secondaryStackItemId]];
            });

            resArr.push({
                row: keyUnitsRow,
                type: Const.STACKS_NUMBER_TYPE_EXACTLY,
                obj: stackItem.isSecondaryStackTypeDouble() ? 2 : 1,
            });

            if (stackItem.isSecondaryStackTypeDouble()) {
                // To make algo take 4 different players
                // if secondary stack with type 2 takes both stacks from one game
                const rulesBuilder = new DoubleSecondaryStackCompatibleTypesRulesBuilder({
                    combosMap: srCombosMap[stackItem.getId()],
                    playersList,
                    checkIfPlayerOnDoublePosition,
                });
                rulesBuilder.createRules();

                const comboIds = rulesBuilder.getComboIds();
                comboIds.map((id) => (objectiveRule[id] = 0));

                resArr = [...resArr, ...rulesBuilder.getRules()];
            }

            this.store.patchObj('additionalObjective', {
                correlationStackingSecondaryRule: {...objectiveRule},
            });
        }
        // console.log("correlationStackingSecondaryRule\n", [...resArr]);
        return [...resArr];
    }

    // correlationStackingRestrictedPlayers2({incomingValues = {}}) {
    //
    //     const {
    //         playersData: {preparedPlayers},
    //         correlationStacking: {
    //             players: csPlayers
    //         }
    //     } = Action.getState();
    //     const primaryStackGameMap = this.store.get('cs.pr.combosMap', {});
    //     console.log('primaryStackGameMap',{...primaryStackGameMap});
    //     const players = preparedPlayers.groupedBy.playerId();
    //
    //     const rulesBuilder = new PlayersRestrictionRulesBuilder(csPlayers, players);
    //
    //     const stackItem = this.store.get('cs.currentItem');
    //
    //     if (!stackItem) {
    //         return rulesBuilder.getRules();
    //     }
    //
    //     rulesBuilder.setStackItem(stackItem);
    //
    //     const config = this.store.get('cs.sr.config', {});
    //
    //     if (!config.hasOwnProperty(stackItem.getId())) {
    //         return rulesBuilder.getRules();
    //     }
    //
    //     const secondaryStackRules = config[stackItem.getId()];
    //
    //     if (!isEmpty(secondaryStackRules)) {
    //         const {gamesMap} = secondaryStackRules;
    //         console.log("gamesMap", {...gamesMap});
    //         rulesBuilder.setGamesMap(gamesMap);
    //     }
    //
    //     const resArr = rulesBuilder.getRules();
    //     // console.log("correlationStackingRestrictedPlayers\n", [...resArr]);
    //     return [...resArr];
    // }

    correlationStackingRestrictedPlayers({incomingValues: {checkIfPlayerOnDoublePosition = false}}) {
        const {
            correlationStacking: {players: csPlayers, settings: csSettings},
            playersData: {players},
            playersExclusion,
            sportConfig,
            lineupGenerator: {settings},
        } = Action.getState();

        if (settings.isRelaxBuildRulesUsed()) {
            return [];
        }

        const rulesBuilder = new RestrictedPlayersSimplexConstraintsBuilder({
            csPlayers,
            players,
            playersExclusion,
            checkIfPlayerOnDoublePosition,
            sportConfig,
        });

        const stackItem = this.store.get('cs.currentItem');

        if (stackItem) {
            let combosMap = this.store.get('cs.pr.combosMap', {});
            const srCombosMap = this.store.get('cs.sr.combosMap', {});

            if (srCombosMap.hasOwnProperty(stackItem.getId())) {
                combosMap = {
                    ...combosMap,
                    ...srCombosMap[stackItem.getId()],
                };
            }
            // console.log('combosMap',{...combosMap});
            rulesBuilder.setCombosMap(combosMap);
        } else if (csSettings.isUniversalRuleUsed()) {
            const urCombosMap = this.store.get('cs.ur.combosMap', {});

            rulesBuilder.setCombosMap(urCombosMap);
        }

        const rules = rulesBuilder.getRules();
        // console.log("correlationStackingRestrictedPlayers\n", [...rules]);
        return [...rules];
    }

    correlationStackingUniversalRule({incomingValues: {checkIfPlayerOnDoublePosition = false}}) {
        const {
            sportConfig,
            correlationStacking: {settings: csSettings},
            playersData: {preparedPlayers},
            lineupGenerator: {settings},
        } = Action.getState();

        const forbiddenPlayers = this.store.get('playersForbiddenByExposureMax', []);
        const stackItem = this.store.get('cs.currentItem');
        // Rule is used only when primary stack is empty.
        if (
            settings.isRelaxBuildRulesUsed() ||
            !csSettings.isEnabled() ||
            !csSettings.isUniversalRuleUsed() ||
            !!stackItem
        ) {
            return [];
        }

        const players = preparedPlayers.groupedBy.playerId();

        function isPlayerOnSinglePosition(playerId) {
            if (!checkIfPlayerOnDoublePosition) {
                return true;
            }
            return !(players.hasOwnProperty(playerId) && isPlayerOnDoublePosition(players[playerId]));
        }

        function getSimplexUnitNameByPlayerIdAndPositionName(playerId, positionName) {
            if (isPlayerOnSinglePosition(playerId)) {
                return getSimplexUnitName(playerId);
            }

            const position = sportConfig.getPositionByName(positionName);

            if (isEmpty(position)) {
                return getSimplexUnitName(playerId);
            }

            const [positionKey] = position.allowedPositions;

            if (!positionKey) {
                return getSimplexUnitName(playerId);
            }

            return getSimplexUnitName([playerId, positionKey]);
        }

        function getAtLeastRule(keyUnitId, playerGroups, minStakingUnitsQuantity = -1) {
            const builder = RuleBuilder.fromAtLeast(0);
            builder.addVar(keyUnitId, minStakingUnitsQuantity);
            playerGroups.map((item) =>
                item.playerIds.map((playerId) =>
                    builder.addVar(getSimplexUnitNameByPlayerIdAndPositionName(playerId, item.positionName), 1),
                ),
            );

            return builder.createRule();
        }

        function extractPlayerIds(playersByPositions, positions) {
            const result = [];
            Object.keys(playersByPositions).map((positionName) => {
                const duplicates = arrayDuplicates(positionName.split('/'), positions);
                if (isEmpty(duplicates)) {
                    return;
                }

                playersByPositions[positionName].map((p) => {
                    if (!inArray(p.getId(), forbiddenPlayers)) {
                        result.push(p.getId());
                    }
                });
            });

            return result;
        }

        function formPlayerGroupsByPositions(playersGroupedByPosition, positionNames) {
            const playerGroups = [];
            positionNames.map((positionName) => {
                const playerIds = extractPlayerIds(playersGroupedByPosition, [positionName]);
                if (!isEmpty(playerIds)) {
                    playerGroups.push({positionName, playerIds});
                }
            });

            return playerGroups;
        }

        const combinations = csSettings.getUniversalStackCombinations();
        const gamesMap = preparedPlayers.groupedBy.gameTeamPosName();
        // console.log('gamesMap',{...gamesMap});
        let rules = [];
        const keyUnitIds = [];
        const stackItemRules = {};
        const keyUnitsVars = {};
        const combosMap = {};
        Object.keys(gamesMap).map((gameId) => {
            const [firstTeam, secondTeam] = Object.values(gamesMap[gameId]);
            keyUnitsVars[gameId] = {};

            combosMap[gameId] = {
                [firstTeam.name]: [],
                [secondTeam.name]: [],
            };

            Object.values(gamesMap[gameId]).map((team) => {
                const keyPlayerIds = extractPlayerIds(team.positions, ['QB']);
                const playerGroups = formPlayerGroupsByPositions(team.positions, ['WR', 'TE']);

                if (isEmpty(keyPlayerIds) || isEmpty(playerGroups)) {
                    return;
                }

                keyPlayerIds.map((keyPlayerId) => {
                    const keyUnitId = Const.LP_CS_UNIVERSAL_STACK_GROUP_PREFIX + keyPlayerId;

                    const keyPlayerRuleBuilder = RuleBuilder.fromAtLeast(0);
                    keyPlayerRuleBuilder.addVar(keyUnitId, -1);
                    keyPlayerRuleBuilder.addVar(getSimplexUnitNameByPlayerIdAndPositionName(keyPlayerId, 'QB'), 1);

                    const ruleBuilder = RuleBuilder.fromAtLeast(0);
                    ruleBuilder.addVar(keyUnitId, -1);

                    playerGroups.map((item) =>
                        item.playerIds.map((playerId) => {
                            ruleBuilder.addVar(
                                getSimplexUnitNameByPlayerIdAndPositionName(playerId, item.positionName),
                                1,
                            );
                        }),
                    );

                    rules.push(keyPlayerRuleBuilder.createRule());
                    rules.push(ruleBuilder.createRule());

                    keyUnitIds.push(keyUnitId);
                    keyUnitsVars[gameId][keyUnitId] = 1;

                    const playerIds = [keyPlayerId];
                    playerGroups.map((item) => item.playerIds.map((playerId) => playerIds.push(playerId)));

                    combosMap[gameId][team.name].push({keyUnitId, playerIds});
                });
            });

            Object.keys(combinations).map((type) => {
                if (!combinations[type]) {
                    return;
                }

                const {mainTeamPositions, opponentTeamPositions} = Const.SECONDARY_STACK_COMBINATION_TYPES[type];
                const typeNumber = Number(type);

                if (typeNumber === Const.SECONDARY_STACK_COMBINATION_TYPE_1) {
                    {
                        const ftPlayerGroups = formPlayerGroupsByPositions(firstTeam.positions, mainTeamPositions);
                        const stPlayerGroups = formPlayerGroupsByPositions(secondTeam.positions, opponentTeamPositions);

                        if (!isEmpty(ftPlayerGroups) && !isEmpty(stPlayerGroups)) {
                            const keyUnitId = getCSComboVarName(['', firstTeam.name, 1]);

                            keyUnitsVars[gameId][keyUnitId] = 1;

                            let playerIds = [];
                            ftPlayerGroups.map((item) => (playerIds = [...playerIds, ...item.playerIds]));
                            stPlayerGroups.map((item) => (playerIds = [...playerIds, ...item.playerIds]));

                            combosMap[gameId][firstTeam.name].push({keyUnitId, playerIds});

                            stackItemRules[keyUnitId] = [
                                getAtLeastRule(keyUnitId, ftPlayerGroups),
                                getAtLeastRule(keyUnitId, stPlayerGroups),
                            ];
                        }
                    }
                    {
                        const ftPlayerGroups = formPlayerGroupsByPositions(firstTeam.positions, opponentTeamPositions);
                        const stPlayerGroups = formPlayerGroupsByPositions(secondTeam.positions, mainTeamPositions);

                        if (!isEmpty(ftPlayerGroups) && !isEmpty(stPlayerGroups)) {
                            const keyUnitId = getCSComboVarName(['', secondTeam.name, 1]);

                            keyUnitsVars[gameId][keyUnitId] = 1;

                            let playerIds = [];
                            ftPlayerGroups.map((item) => (playerIds = [...playerIds, ...item.playerIds]));
                            stPlayerGroups.map((item) => (playerIds = [...playerIds, ...item.playerIds]));

                            combosMap[gameId][secondTeam.name].push({keyUnitId, playerIds});

                            stackItemRules[keyUnitId] = [
                                getAtLeastRule(keyUnitId, ftPlayerGroups),
                                getAtLeastRule(keyUnitId, stPlayerGroups),
                            ];
                        }
                    }
                } else if (typeNumber === Const.SECONDARY_STACK_COMBINATION_TYPE_2) {
                    {
                        const ftPlayerGroups = formPlayerGroupsByPositions(firstTeam.positions, mainTeamPositions);
                        const stPlayerGroups = formPlayerGroupsByPositions(secondTeam.positions, opponentTeamPositions);

                        if (!isEmpty(ftPlayerGroups) && !isEmpty(stPlayerGroups)) {
                            const keyUnitId = getCSComboVarName(['', firstTeam.name, 2]);

                            keyUnitsVars[gameId][keyUnitId] = 1;

                            let playerIds = [];
                            ftPlayerGroups.map((item) => (playerIds = [...playerIds, ...item.playerIds]));
                            stPlayerGroups.map((item) => (playerIds = [...playerIds, ...item.playerIds]));

                            combosMap[gameId][firstTeam.name].push({keyUnitId, playerIds});

                            stackItemRules[keyUnitId] = [
                                getAtLeastRule(keyUnitId, ftPlayerGroups),
                                getAtLeastRule(keyUnitId, stPlayerGroups),
                            ];
                        }
                    }
                    {
                        const ftPlayerGroups = formPlayerGroupsByPositions(firstTeam.positions, opponentTeamPositions);
                        const stPlayerGroups = formPlayerGroupsByPositions(secondTeam.positions, mainTeamPositions);

                        if (!isEmpty(ftPlayerGroups) && !isEmpty(stPlayerGroups)) {
                            const keyUnitId = getCSComboVarName(['', secondTeam.name, 2]);

                            keyUnitsVars[gameId][keyUnitId] = 1;

                            let playerIds = [];
                            ftPlayerGroups.map((item) => (playerIds = [...playerIds, ...item.playerIds]));
                            stPlayerGroups.map((item) => (playerIds = [...playerIds, ...item.playerIds]));

                            combosMap[gameId][secondTeam.name].push({keyUnitId, playerIds});

                            stackItemRules[keyUnitId] = [
                                getAtLeastRule(keyUnitId, ftPlayerGroups),
                                getAtLeastRule(keyUnitId, stPlayerGroups),
                            ];
                        }
                    }
                } else if (typeNumber === Const.SECONDARY_STACK_COMBINATION_TYPE_3) {
                    {
                        const [firstPosition, secondPosition] = mainTeamPositions;
                        let fpPlayerGroups = [],
                            spPlayerGroups = [];
                        if (firstPosition) {
                            fpPlayerGroups = formPlayerGroupsByPositions(firstTeam.positions, [firstPosition]);
                        }
                        if (secondPosition) {
                            spPlayerGroups = formPlayerGroupsByPositions(firstTeam.positions, [secondPosition]);
                        }

                        if (!isEmpty(fpPlayerGroups) && !isEmpty(spPlayerGroups)) {
                            const keyUnitId = getCSComboVarName(['', firstTeam.name, 3]);

                            keyUnitsVars[gameId][keyUnitId] = 1;

                            let playerIds = [];
                            fpPlayerGroups.map((item) => (playerIds = [...playerIds, ...item.playerIds]));
                            spPlayerGroups.map((item) => (playerIds = [...playerIds, ...item.playerIds]));

                            combosMap[gameId][firstTeam.name].push({keyUnitId, playerIds});

                            stackItemRules[keyUnitId] = [
                                getAtLeastRule(keyUnitId, fpPlayerGroups),
                                getAtLeastRule(keyUnitId, spPlayerGroups),
                            ];
                        }
                    }
                    {
                        const [firstPosition, secondPosition] = mainTeamPositions;
                        let fpPlayerGroups = [],
                            spPlayerGroups = [];
                        if (firstPosition) {
                            fpPlayerGroups = formPlayerGroupsByPositions(secondTeam.positions, [firstPosition]);
                        }
                        if (secondPosition) {
                            spPlayerGroups = formPlayerGroupsByPositions(secondTeam.positions, [secondPosition]);
                        }

                        if (!isEmpty(fpPlayerGroups) && !isEmpty(spPlayerGroups)) {
                            const keyUnitId = getCSComboVarName(['', secondTeam.name, 3]);

                            keyUnitsVars[gameId][keyUnitId] = 1;

                            let playerIds = [];
                            fpPlayerGroups.map((item) => (playerIds = [...playerIds, ...item.playerIds]));
                            spPlayerGroups.map((item) => (playerIds = [...playerIds, ...item.playerIds]));

                            combosMap[gameId][secondTeam.name].push({keyUnitId, playerIds});

                            stackItemRules[keyUnitId] = [
                                getAtLeastRule(keyUnitId, fpPlayerGroups),
                                getAtLeastRule(keyUnitId, spPlayerGroups),
                            ];
                        }
                    }
                } else if (typeNumber === Const.SECONDARY_STACK_COMBINATION_TYPE_4) {
                    {
                        const [firstPosition, ...secondPositions] = mainTeamPositions;
                        let fpPlayerGroups = [],
                            spPlayerGroups = [];
                        if (firstPosition) {
                            fpPlayerGroups = formPlayerGroupsByPositions(firstTeam.positions, [firstPosition]);
                        }
                        if (!isEmpty(secondPositions)) {
                            spPlayerGroups = formPlayerGroupsByPositions(firstTeam.positions, secondPositions);
                        }

                        if (!isEmpty(fpPlayerGroups) && !isEmpty(spPlayerGroups)) {
                            const keyUnitId = getCSComboVarName(['', firstTeam.name, 4]);

                            keyUnitsVars[gameId][keyUnitId] = 1;

                            let playerIds = [];
                            fpPlayerGroups.map((item) => (playerIds = [...playerIds, ...item.playerIds]));
                            spPlayerGroups.map((item) => (playerIds = [...playerIds, ...item.playerIds]));

                            combosMap[gameId][firstTeam.name].push({keyUnitId, playerIds});

                            stackItemRules[keyUnitId] = [
                                getAtLeastRule(keyUnitId, fpPlayerGroups),
                                getAtLeastRule(keyUnitId, spPlayerGroups),
                            ];
                        }
                    }
                    {
                        const [firstPosition, ...secondPositions] = mainTeamPositions;
                        let fpPlayerGroups = [],
                            spPlayerGroups = [];
                        if (firstPosition) {
                            fpPlayerGroups = formPlayerGroupsByPositions(secondTeam.positions, [firstPosition]);
                        }
                        if (!isEmpty(secondPositions)) {
                            spPlayerGroups = formPlayerGroupsByPositions(secondTeam.positions, secondPositions);
                        }

                        if (!isEmpty(fpPlayerGroups) && !isEmpty(spPlayerGroups)) {
                            const keyUnitId = getCSComboVarName(['', secondTeam.name, 4]);

                            keyUnitsVars[gameId][keyUnitId] = 1;

                            let playerIds = [];
                            fpPlayerGroups.map((item) => (playerIds = [...playerIds, ...item.playerIds]));
                            spPlayerGroups.map((item) => (playerIds = [...playerIds, ...item.playerIds]));

                            combosMap[gameId][secondTeam.name].push({keyUnitId, playerIds});

                            stackItemRules[keyUnitId] = [
                                getAtLeastRule(keyUnitId, fpPlayerGroups),
                                getAtLeastRule(keyUnitId, spPlayerGroups),
                            ];
                        }
                    }
                } else if (typeNumber === Const.SECONDARY_STACK_COMBINATION_TYPE_5) {
                    {
                        const playerGroups = formPlayerGroupsByPositions(firstTeam.positions, mainTeamPositions);

                        if (!isEmpty(playerGroups)) {
                            const keyUnitId =
                                Const.LP_CS_COMBINATION_PREFIX + ['', firstTeam.name, 5].join(Const.LP_ITEM_DELIMITER);

                            keyUnitsVars[gameId][keyUnitId] = 1;

                            let playerIds = [];
                            playerGroups.map((item) => (playerIds = [...playerIds, ...item.playerIds]));

                            combosMap[gameId][firstTeam.name].push({keyUnitId, playerIds});

                            stackItemRules[keyUnitId] = [getAtLeastRule(keyUnitId, playerGroups, -2)];
                        }
                    }
                    {
                        const playerGroups = formPlayerGroupsByPositions(secondTeam.positions, mainTeamPositions);

                        if (!isEmpty(playerGroups)) {
                            const keyUnitId =
                                Const.LP_CS_COMBINATION_PREFIX + ['', secondTeam.name, 5].join(Const.LP_ITEM_DELIMITER);

                            keyUnitsVars[gameId][keyUnitId] = 1;

                            let playerIds = [];
                            playerGroups.map((item) => (playerIds = [...playerIds, ...item.playerIds]));

                            combosMap[gameId][secondTeam.name].push({keyUnitId, playerIds});

                            stackItemRules[keyUnitId] = [getAtLeastRule(keyUnitId, playerGroups, -2)];
                        }
                    }
                }
            });
        });

        const objectiveRule = {};

        if (!isEmpty(keyUnitIds)) {
            const keyPlayersRuleBuilder = RuleBuilder.fromExactly(1);
            keyUnitIds.map((id) => keyPlayersRuleBuilder.addVar(id, 1));
            keyUnitIds.map((id) => (objectiveRule[id] = 0));

            rules.push(keyPlayersRuleBuilder.createRule());
        }
        // console.log('keyUnitsVars',{...keyUnitsVars});
        Object.values(keyUnitsVars).map((vars) => {
            if (!isEmpty(vars)) {
                const keyUnitsRuleBuilder = RuleBuilder.fromAtMost(1);
                keyUnitsRuleBuilder.addVars(vars);

                rules.push(keyUnitsRuleBuilder.createRule());
            }
        });

        const comboKeyUnitsRuleBuilder = RuleBuilder.fromExactly(1);
        Object.keys(stackItemRules).map((unitId) => {
            comboKeyUnitsRuleBuilder.addVar(unitId, 1);
            objectiveRule[unitId] = 0;
            rules = [...rules, ...stackItemRules[unitId]];
        });

        if (comboKeyUnitsRuleBuilder.isRuleCreatable()) {
            rules.push(comboKeyUnitsRuleBuilder.createRule());
        }

        this.store.patchObj('additionalObjective', {
            correlationStackingUniversalRule: {...objectiveRule},
        });

        this.store.set('cs.ur.combosMap', combosMap);
        // console.log("correlationStackingUniversalRule\n", [...rules]);
        return [...rules];
    }

    stackHitters({incomingValues: {getUnitIdsByPlayer = (player) => [player.getId()]}}) {
        const {
            stacks: {hitters},
            lineupGenerator: {settings},
            playersData: {preparedPlayers, players},
            lineupsQuantity,
        } = Action.getState();
        let rules = [];

        if (settings.isRelaxBuildRulesUsed() || !hitters.isEnabled()) {
            return rules;
        }

        let hittersStack = this.store.get('hittersStack', {});
        const playersGroupedByTeam = preparedPlayers.groupedBy.property('team');
        const forbiddenPlayers = this.store.get('playersForbiddenByExposureMax', []);
        let forbiddenUnits = forbiddenPlayers.map((pId) => getSimplexUnitName(pId));

        const createStackConstraintsObj = function (groupNumber, fromLineupsQuantity, parentTeamName = null) {
            let obj = {
                    teams: [],
                    lastUsedTeam: null,
                },
                stack = {...hitters.getGroup(groupNumber)},
                items = [...stack.items];

            items.sort(sortByProperty('expMin'));
            items.map((item) => {
                if (parentTeamName === item.team) {
                    return;
                }
                if (!playersGroupedByTeam.hasOwnProperty(item.team)) {
                    return;
                }
                let resObj = {
                    row: {},
                    type: Const.STACKS_NUMBER_TYPE_EXACTLY,
                    obj: Number(stack.playersQuantity),
                };
                if (isEmpty(item.playerIds)) {
                    playersGroupedByTeam[item.team].map((player) => {
                        if (!player.isPitcher()) {
                            const playerUIDs = getUnitIdsByPlayer(player);
                            playerUIDs.map((pid) => {
                                resObj.row[getSimplexUnitName(pid)] = 1;
                            });
                        }
                    });
                } else {
                    item.playerIds.map((playerId) => {
                        const player = players.getPlayer(playerId);

                        if (player === null) {
                            return;
                        }

                        const playerUIDs = getUnitIdsByPlayer(player);
                        playerUIDs.map((pid) => {
                            resObj.row[getSimplexUnitName(pid)] = 1;
                        });
                    });
                }

                if (isEmpty(resObj.row)) {
                    return;
                }

                obj.teams.push({
                    name: item.team,
                    playersObj: resObj,
                    expMin: item.expMin,
                    reservedLineups: calcAndRoundDownValByPct(fromLineupsQuantity, item.expMin),
                });
            });

            return {...obj};
        };

        const countAvailablePlayers = function (row) {
            if (isEmpty(forbiddenUnits)) {
                return row.length;
            }

            let quantity = 0;

            row.map((unitId) => {
                if (!inArray(unitId, forbiddenUnits)) {
                    quantity++;
                }
            });

            return quantity;
        };

        const createConstraints = function (comingStack) {
            // console.log('comingStack',{...comingStack});
            let iterator = makeIterator(comingStack.teams);
            if (null !== comingStack.lastUsedTeam) {
                iterator.setKey(comingStack.lastUsedTeam);
                iterator.next();
            }

            let rules = [];
            for (let i = 0; i < comingStack.teams.length; i++) {
                if (!iterator.valid()) {
                    iterator.rewind();
                }

                const team = iterator.current();
                const {playersObj} = team;
                const availablePlayersQuantity = countAvailablePlayers(Object.keys(playersObj.row));
                // console.log('availablePlayersQuantity',availablePlayersQuantity);
                if (0 < team.reservedLineups && playersObj.obj <= availablePlayersQuantity) {
                    // console.log('team',team);
                    rules.push(team.playersObj);
                    team.reservedLineups--;
                    comingStack.lastUsedTeam = iterator.key();
                    if (!isEmpty(team.stack)) {
                        rules = [...rules, ...createConstraints(team.stack)];
                    }
                    break;
                }

                iterator.next();
            }

            return [...rules];
        };

        const distributeFreeLineups = function (comingStack, lineupsQuantity) {
            let freeLineups = lineupsQuantity;
            comingStack.teams.map((team) => {
                freeLineups -= team.reservedLineups;
            });

            if (freeLineups) {
                let iterator = makeIterator(comingStack.teams),
                    filledTeamsQuantity = 0;
                for (let i = 0; i < freeLineups; i++) {
                    if (!iterator.valid()) {
                        iterator.rewind();
                    }

                    let team = iterator.current(),
                        reservedLineupsPercent = calcPercentage(lineupsQuantity, team.reservedLineups);

                    if (team.expMin > reservedLineupsPercent) {
                        team.reservedLineups++;
                    } else {
                        filledTeamsQuantity++;
                    }

                    if (filledTeamsQuantity >= comingStack.teams.length) {
                        break;
                    }

                    iterator.next();
                }
            }

            return comingStack;
        };

        if (isEmpty(hittersStack)) {
            hittersStack = createStackConstraintsObj(Const.FIRST_HITTER_GROUP, lineupsQuantity.getLineupsQuantity());

            hittersStack = distributeFreeLineups({...hittersStack}, lineupsQuantity.getLineupsQuantity());
            if (hitters.isDoubleStackModeEnabled()) {
                hittersStack.teams.map((team) => {
                    let secondStack = createStackConstraintsObj(
                        Const.SECOND_HITTER_GROUP,
                        team.reservedLineups,
                        team.name,
                    );
                    if (!isEmpty(secondStack.teams)) {
                        team.stack = distributeFreeLineups({...secondStack}, team.reservedLineups);
                    }
                });
            }
        }

        rules = createConstraints(hittersStack);

        this.store.set('hittersStack', hittersStack);

        return [...rules];
    }

    // getUnitIdsByPlayer was added because DK has players on double position.
    nonControlledHittersStacking({incomingValues: {getUnitIdsByPlayer = (player) => [player.getId()]}}) {
        const {
            nonControlledHitterStacking: {settings: stacking, players: restrictedPlayers},
            projectionPointsType,
            lineupTeamsCombinations,
            lineupsData: {rawLineups},
            lineupsQuantity,
            playersData: {preparedPlayers, mlbFilters},
            lineupGenerator: {settings},
        } = Action.getState();
        const teams = preparedPlayers.groupedBy.property('team');
        const players = preparedPlayers.groupedBy.playerId();

        const ConstraintsBuilder = NonControlledHittersStackingConstraintsBuilderFactory.getBuilder();
        const constraintsBuilder = new ConstraintsBuilder({
            stacking,
            mappedPlayersByTeams: teams,
            getUnitIdsByPlayer,
        });

        let resArr = [];
        if (settings.isRelaxBuildRulesUsed() || !stacking.isEnabled()) {
            return constraintsBuilder.createDisabledPlayersConstraints({
                restrictedPlayers,
                players,
            });
        }

        const buildCombinationsConstraints = function () {
            let constraints = this.store.get('nonControlledHittersStacking.constraints', {});
            if (isEmpty(constraints)) {
                constraints = constraintsBuilder.createPreparedTeamsConstraints({
                    mlbFilters,
                    lineupsQuantity,
                });

                this.store.set('nonControlledHittersStacking.constraints', {...constraints});
            }

            return {...constraints};
        }.bind(this);

        const buildCombinationsQuantity = function () {
            let combinationsQuantity = this.store.get('nonControlledHittersStacking.combinationsQuantity', []);
            if (isEmpty(combinationsQuantity)) {
                combinationsQuantity = constraintsBuilder.buildCombinationsQuantity({
                    lineupsQuantity,
                });

                this.store.set('nonControlledHittersStacking.combinationsQuantity', [...combinationsQuantity]);
            }

            return [...combinationsQuantity];
        }.bind(this);

        const chooseCombinationConstraints = function (comboConstraints) {
            const lineupKey = `lineup-${rawLineups.getQuantity()}`;
            const combinationsHistory = this.store.get('nonControlledHittersStacking.combinationsHistory', {});

            if (lineupKey in combinationsHistory) {
                return {...comboConstraints[combinationsHistory[lineupKey]]};
            }

            const combinationsQuantity = buildCombinationsQuantity();

            combinationsQuantity.sort(sortByProperty('lineupsQuantity', 'desc'));
            const combinationQuantity = combinationsQuantity[0];

            if (0 >= combinationQuantity.lineupsQuantity) {
                return {};
            }

            combinationsHistory[lineupKey] = combinationQuantity.comboKey;
            this.store.set('nonControlledHittersStacking.combinationsHistory', {...combinationsHistory});

            combinationQuantity.lineupsQuantity--;

            return {...comboConstraints[combinationQuantity.comboKey]};
        }.bind(this);

        const comboConstraints = buildCombinationsConstraints();
        // console.log('comboConstraints',{...comboConstraints});
        const constraints = chooseCombinationConstraints(comboConstraints);
        // console.log('constraints',{...constraints});
        const filteredConstraints = constraintsBuilder.createConstraintsForExcludedTeamsByExposureMax({
            teamsConstraints: constraints,
            lineupsQuantity,
            usedGroups: this.store.get('usedTeamGroups', []),
        });
        // console.log('filteredConstraints',{...filteredConstraints});
        const doubleTeamsConstraints = constraintsBuilder.createPreparedDoubleTeamsConstraints({
            constraints: filteredConstraints,
        });
        // console.log('doubleTeamsConstraints',[...doubleTeamsConstraints]);
        const restrictedPlayersConstraints = constraintsBuilder.createRestrictedPlayersConstraints({
            constraints: filteredConstraints,
            restrictedPlayers,
            players,
        });
        // console.log('restrictedPlayersConstraints',[...restrictedPlayersConstraints]);
        const groupsConstraints = constraintsBuilder.createPreparedGroupsConstraints({
            constraints: filteredConstraints,
            doubleTeamsConstraints,
        });
        // console.log('groupsConstraints',[...groupsConstraints]);
        const desirableConstraints = constraintsBuilder.createConstraintsForIncludedTeamsByExposureMin({
            constraints: filteredConstraints,
            lineupsQuantity,
            usedGroups: this.store.get('usedTeamGroups', []),
        });
        // console.log('desirableConstraints',[...desirableConstraints]);
        const comboTeamsConstraints = constraintsBuilder.createConstraintsForExcludeTeamsCombinations({
            constraints: filteredConstraints,
            lineupTeamsCombinations,
            rawLineups,
            lineupsQuantity,
        });
        // console.log('comboTeamsConstraints',[...comboTeamsConstraints])
        let generalObjective = {};
        Object.values(filteredConstraints).map((group) => {
            if (!isEmpty(group)) {
                Object.values(group).map((item) => {
                    generalObjective[item.groupName] = 0;
                    resArr.push({...item.constraint});
                });
            }
        });

        resArr = [
            ...resArr,
            ...groupsConstraints,
            ...doubleTeamsConstraints,
            ...restrictedPlayersConstraints,
            ...desirableConstraints,
            ...comboTeamsConstraints,
        ];
        // console.log('resArr',[...resArr])
        let playersObjective = {};
        if (stacking.isTeamsChosenRandomly()) {
            playersObjective = constraintsBuilder.createObjectiveForRandomlyChosenPlayers({
                constraints: filteredConstraints,
                projectionPointsPropName: projectionPointsType.getProjectionPointsType(),
            });
        }

        this.store.patchObj('additionalObjective', {
            'nonControlledStack.generalObjective': {...generalObjective},
            'nonControlledStack.playersObjective': {...playersObjective},
        });

        return [...resArr];
    }

    nhlTeamStacking({incomingValues: {}}) {
        const {
            nhlTeamStacking: stacking,
            projectionPointsType,
            lineupTeamsCombinations,
            lineupsData: {rawLineups},
            lineupsQuantity,
            playersData: {preparedPlayers, lineGroupFilter},
            lineupGenerator: {settings},
            sportConfig,
        } = Action.getState();

        let simplexRules = [];
        if (settings.isRelaxBuildRulesUsed() || !stacking.isEnabled()) {
            return simplexRules;
        }

        const teams = preparedPlayers.groupedBy.property('team');

        const ConstraintsBuilder = NhlTeamStackingConstraintsBuilderFactory.getBuilder();
        const constraintsBuilder = new ConstraintsBuilder({
            sportConfig,
            stacking,
            playersGroupedByTeams: teams,
        });

        const createConstraintCombinations = function () {
            let constraints = this.store.get('nhlTeamStacking.constraints', {});

            if (isEmpty(constraints)) {
                constraints = constraintsBuilder.createConstraintCombinations({
                    lineGroupFilter,
                    lineupsQuantity,
                });

                this.store.set('nhlTeamStacking.constraints', {...constraints});
            }

            return {...constraints};
        }.bind(this);

        const calcCombinationsQuantity = function () {
            let combinationsQuantity = this.store.get('nhlTeamStacking.combinationsQuantity', []);

            if (isEmpty(combinationsQuantity)) {
                combinationsQuantity = constraintsBuilder.calcCombinationsQuantity({
                    lineupsQuantity,
                });

                this.store.set('nhlTeamStacking.combinationsQuantity', [...combinationsQuantity]);
            }

            return [...combinationsQuantity];
        }.bind(this);

        const chooseCombinationConstraints = function (comboConstraints) {
            const combinationsQuantity = calcCombinationsQuantity();

            combinationsQuantity.sort(sortByProperty('minLineupsQuantity', 'desc'));

            for (let combinationQuantity of combinationsQuantity) {
                if (0 < combinationQuantity.minLineupsQuantity) {
                    combinationQuantity.minLineupsQuantity--;
                    combinationQuantity.maxLineupsQuantity--;

                    return {
                        constraints: {...comboConstraints[combinationQuantity.comboKey]},
                        key: combinationQuantity.comboKey,
                    };
                }
            }

            for (let combinationQuantity of combinationsQuantity) {
                if (0 < combinationQuantity.maxLineupsQuantity) {
                    combinationQuantity.minLineupsQuantity--;
                    combinationQuantity.maxLineupsQuantity--;

                    return {
                        constraints: {...comboConstraints[combinationQuantity.comboKey]},
                        key: combinationQuantity.comboKey,
                    };
                }
            }

            return {constraints: {}, key: ''};
        }.bind(this);

        const constraintCombinations = createConstraintCombinations();
        // console.log('constraintCombinations',{...constraintCombinations});
        const combinationConstraints = chooseCombinationConstraints(constraintCombinations);
        // console.log('combinationConstraints',{...combinationConstraints});
        const filteredConstraints = constraintsBuilder.createConstraintsForExcludedTeamsByExposureMax({
            constraints: combinationConstraints.constraints,
            lineupsQuantity,
            usedTeamNames: this.store.get('usedSubGroupTeams', []),
        });
        // console.log('filteredConstraints',{...filteredConstraints});
        const doubleTeamsConstraints = constraintsBuilder.createDoubleTeamsConstraints({
            constraints: filteredConstraints,
        });
        // console.log('doubleTeamsConstraints',[...doubleTeamsConstraints]);
        const groupsConstraints = constraintsBuilder.createGroupsConstraints({
            constraints: filteredConstraints,
            comboKey: combinationConstraints.key,
        });
        // console.log('groupsConstraints',[...groupsConstraints]);
        const desirableConstraints = constraintsBuilder.createConstraintsForIncludedTeamsByExposureMin({
            constraints: filteredConstraints,
            lineupsQuantity,
            usedTeamNames: this.store.get('usedSubGroupTeams', []),
        });
        // console.log('desirableConstraints',[...desirableConstraints]);
        const comboTeamsConstraints = constraintsBuilder.createConstraintsForExcludeTeamsCombinations({
            constraints: filteredConstraints,
            lineupTeamsCombinations,
            rawLineups,
            lineupsQuantity,
        });
        // console.log('comboTeamsConstraints',[...comboTeamsConstraints])
        let generalObjective = {};
        Object.values(filteredConstraints).map((teamConstraints) => {
            teamConstraints.subGroupNames.map((subGroupName) => (generalObjective[subGroupName] = 0));

            const {goalie, ...groupedConstraints} = teamConstraints.constraints;

            Object.values(groupedConstraints).map((constraints) => {
                Object.values(constraints).map((item) => {
                    item.constraints.map((constraint) => simplexRules.push({...constraint}));
                });
            });

            !isEmpty(goalie) && simplexRules.push({...goalie});
        });

        simplexRules = [
            ...simplexRules,
            ...doubleTeamsConstraints,
            ...desirableConstraints,
            ...comboTeamsConstraints,
            ...groupsConstraints,
        ];

        let playersObjective = {};
        if (stacking.isTeamsChosenRandomly()) {
            playersObjective = constraintsBuilder.createObjectiveForRandomlyChosenPlayers({
                constraints: filteredConstraints,
                projectionPointsPropName: projectionPointsType.getProjectionPointsType(),
            });
        }

        this.store.patchObj('additionalObjective', {
            'nhlTeamStacking.generalObjective': {...generalObjective},
            'nhlTeamStacking.playersObjective': {...playersObjective},
        });
        // console.log("nhlTeamStacking\n",simplexRules);
        return [...simplexRules];
    }

    nhlLegacyTeamStacking({incomingValues: {}}) {
        const {
            nhlLegacyTeamStacking: stacking,
            projectionPointsType,
            lineupTeamsCombinations,
            lineupsData: {rawLineups},
            lineupsQuantity,
            playersData: {preparedPlayers, lineGroupFilter},
            lineupGenerator: {settings},
            sportConfig,
        } = Action.getState();

        let simplexRules = [];
        if (settings.isRelaxBuildRulesUsed() || !stacking.isEnabled()) {
            return simplexRules;
        }

        const teams = preparedPlayers.groupedBy.property('team');

        const ConstraintsBuilder = NhlLegacyTeamStackingConstraintsBuilderFactory.getBuilder();
        const constraintsBuilder = new ConstraintsBuilder({
            sportConfig,
            stacking,
            mappedPlayersByTeams: teams,
        });

        const buildCombinationsConstraints = function () {
            let constraints = this.store.get('nhlLegacyTeamStacking.constraints', {});

            if (isEmpty(constraints)) {
                constraints = constraintsBuilder.createPreparedTeamsConstraints({
                    lineGroupFilter,
                    lineupsQuantity,
                });

                this.store.set('nhlLegacyTeamStacking.constraints', {...constraints});
            }

            return {...constraints};
        }.bind(this);

        const buildCombinationsQuantity = function () {
            let combinationsQuantity = this.store.get('nhlLegacyTeamStacking.combinationsQuantity', []);

            if (isEmpty(combinationsQuantity)) {

                combinationsQuantity = constraintsBuilder.buildCombinationsQuantity({
                    lineupsQuantity,
                });

                this.store.set('nhlLegacyTeamStacking.combinationsQuantity', [...combinationsQuantity]);
            }

            return [...combinationsQuantity];
        }.bind(this);

        const chooseCombinationConstraints = function (comboConstraints) {
            const combinationsQuantity = buildCombinationsQuantity();

            combinationsQuantity.sort(sortByProperty('minLineupsQuantity', 'desc'));

            for (let combinationQuantity of combinationsQuantity) {
                if (0 < combinationQuantity.minLineupsQuantity) {
                    combinationQuantity.minLineupsQuantity--;
                    combinationQuantity.maxLineupsQuantity--;

                    return {...comboConstraints[combinationQuantity.comboKey]};
                }
            }

            for (let combinationQuantity of combinationsQuantity) {
                if (0 < combinationQuantity.maxLineupsQuantity) {
                    combinationQuantity.minLineupsQuantity--;
                    combinationQuantity.maxLineupsQuantity--;

                    return {...comboConstraints[combinationQuantity.comboKey]};
                }
            }

            return {};
        }.bind(this);

        const comboConstraints = buildCombinationsConstraints();
        // console.log('comboConstraints',{...comboConstraints});
        const constraints = chooseCombinationConstraints(comboConstraints);
        // console.log('constraints',{...constraints});
        const filteredConstraints = constraintsBuilder.createConstraintsForExcludedTeamsByExposureMax({
            teamsConstraints: constraints,
            lineupsQuantity,
            usedGroups: this.store.get('usedTeamGroups', []),
        });
        // console.log('filteredConstraints',{...filteredConstraints});
        const doubleTeamsConstraints = constraintsBuilder.createPreparedDoubleTeamsConstraints({
            constraints: filteredConstraints,
        });
        // console.log('doubleTeamsConstraints',[...doubleTeamsConstraints]);
        const groupsConstraints = constraintsBuilder.createPreparedGroupsConstraints({
            constraints: filteredConstraints,
        });
        // console.log('groupsConstraints',[...groupsConstraints]);
        const desirableConstraints = constraintsBuilder.createConstraintsForIncludedTeamsByExposureMin({
            constraints: filteredConstraints,
            lineupsQuantity,
            usedGroups: this.store.get('usedTeamGroups', []),
        });
        // console.log('desirableConstraints',[...desirableConstraints]);
        const comboTeamsConstraints = constraintsBuilder.createConstraintsForExcludeTeamsCombinations({
            constraints: filteredConstraints,
            lineupTeamsCombinations,
            rawLineups,
            lineupsQuantity,
        });
        // console.log('comboTeamsConstraints',[...comboTeamsConstraints])
        let generalObjective = {};
        Object.values(filteredConstraints).map(group => {
            Object.values(group).map(item => {
                generalObjective[item.groupName] = 0;
                item.subGroupNames.map(subGroupName => generalObjective[subGroupName] = 0);
                item.constraints.map(constraint => simplexRules.push({...constraint}));
            });
        });

        simplexRules = [
            ...simplexRules,
            ...doubleTeamsConstraints,
            ...desirableConstraints,
            ...comboTeamsConstraints,
        ];

        if (stacking.isTeamsForcedToStacking()) {
            simplexRules = [...simplexRules, ...groupsConstraints];
        } else {
            const stackingUnitId = 'g_' + Const.LP_NHL_LEGACY_TEAM_STACKING_SUFFIX;
            generalObjective[stackingUnitId] = 100;

            const stackingConstraints = [];
            Object.values(groupsConstraints).map(groupConstraint => {
                const ruleBuilder = RuleBuilder.fromAtLeast(0, groupConstraint.row);
                ruleBuilder.addVar(stackingUnitId, -1);

                stackingConstraints.push(ruleBuilder.createRule());
            });

            simplexRules = [...simplexRules, ...stackingConstraints];
        }

        let playersObjective = {};
        if (stacking.isTeamsChosenRandomly()) {
            playersObjective = constraintsBuilder.createObjectiveForRandomlyChosenPlayers({
                constraints: filteredConstraints,
                projectionPointsPropName: projectionPointsType.getProjectionPointsType(),
            });
        }

        this.store.patchObj('additionalObjective', {
            'nhlLegacyTeamStacking.generalObjective': {...generalObjective},
            'nhlLegacyTeamStacking.playersObjective': {...playersObjective},
        });
        // console.log("nhlLegacyTeamStacking\n",simplexRules);
        return [...simplexRules];
    }

    nhlNameStacking({incomingValues: {}}) {
        const state = Action.getState();
        const {nhlName} = state.stacks;

        if (!nhlName.useStack) {
            return [];
        }

        let nhlNameStack = this.store.get('nhlNameStack', {});
        const {preparedPlayers} = state.playersData;
        const {lineupsQuantity} = state;
        const playersGroupedByTeam = preparedPlayers.groupedBy.property('team');

        const createStackConstraintsObj = function (groupNumber, fromLineupsQuantity, parentTeamName = null) {
            let obj = {
                    teams: [],
                    lastUsedTeam: null,
                },
                stack = {...nhlName.groups[groupNumber]},
                items = [...stack.items];

            items.sort(sortByProperty('expMin'));
            items.map((item) => {
                if (parentTeamName === item.team || !playersGroupedByTeam.hasOwnProperty(item.team)) {
                    return;
                }

                const vars = {};
                if (isEmpty(item.players)) {
                    let players = playersGroupedByTeam[item.team];
                    players.map((player) => {
                        if (!player.isGoalie()) {
                            preparePlayerUID(player).map((playerUId) => (vars[getSimplexUnitName(playerUId)] = 1));
                        }
                    });
                } else {
                    item.players.map((player) => {
                        preparePlayerUID(player).map((playerUId) => (vars[getSimplexUnitName(playerUId)] = 1));
                    });
                }

                if (isEmpty(vars)) {
                    return;
                }

                const ruleBuilder = RuleBuilder.fromExactly(Number(stack.playersQuantity));
                ruleBuilder.addVars(vars);

                obj.teams.push({
                    name: item.team,
                    simplexConstraint: ruleBuilder.createRule(),
                    expMin: item.expMin,
                    reservedLineups: calcAndRoundDownValByPct(fromLineupsQuantity, item.expMin),
                });
            });

            return {...obj};
        };

        const createConstraints = function (comingStack) {
            let iterator = makeIterator(comingStack.teams);
            if (null !== comingStack.lastUsedTeam) {
                iterator.setKey(comingStack.lastUsedTeam);
                iterator.next();
            }

            let constraints = [];
            for (var i = 0; i < comingStack.teams.length; i++) {
                if (!iterator.valid()) {
                    iterator.rewind();
                }

                let team = iterator.current();

                if (0 < team.reservedLineups) {
                    constraints.push(team.simplexConstraint);
                    team.reservedLineups--;
                    comingStack.lastUsedTeam = iterator.key();
                    if (!isEmpty(team.stack)) {
                        createConstraints(team.stack).map((constraint) => constraints.push(constraint));
                    }
                    break;
                }

                iterator.next();
            }
            return constraints;
        };

        const distributeFreeLineups = function (comingStack, lineupsQuantity) {
            let freeLineups = lineupsQuantity;
            comingStack.teams.map((team) => {
                freeLineups -= team.reservedLineups;
            });
            if (freeLineups) {
                let iterator = makeIterator(comingStack.teams),
                    filledTeamsQuantity = 0;
                for (var i = 0; i < freeLineups; i++) {
                    if (!iterator.valid()) {
                        iterator.rewind();
                    }

                    let team = iterator.current(),
                        reservedLineupsPercent = calcPercentage(lineupsQuantity, team.reservedLineups);

                    if (team.expMin > reservedLineupsPercent) {
                        team.reservedLineups++;
                    } else {
                        filledTeamsQuantity++;
                    }

                    if (filledTeamsQuantity >= comingStack.teams.length) {
                        break;
                    }

                    iterator.next();
                }
            }

            return comingStack;
        };

        if (isEmpty(nhlNameStack)) {
            nhlNameStack = createStackConstraintsObj(
                Const.NHL_NAME_STACK_FIRST_GROUP,
                lineupsQuantity.getLineupsQuantity(),
            );
            nhlNameStack = distributeFreeLineups({...nhlNameStack}, lineupsQuantity.getLineupsQuantity());
            if (nhlName.isDoubleStackMode) {
                nhlNameStack.teams.map((team) => {
                    let secondStack = createStackConstraintsObj(
                        Const.NHL_NAME_STACK_SECOND_GROUP,
                        team.reservedLineups,
                        team.name,
                    );
                    if (!isEmpty(secondStack.teams)) {
                        team.stack = distributeFreeLineups({...secondStack}, team.reservedLineups);
                    }
                });
            }
        }

        const result = createConstraints(nhlNameStack);

        this.store.set('nhlNameStack', nhlNameStack);
        // console.log("result\n",result);
        return result;
    }

    nhlNonControlledStacking({incomingValues: {}}) {
        const state = Action.getState();
        const {nonControlled} = state.stacks;
        const {lineupsQuantity} = state;
        const {preparedPlayers, filters: playersFilter} = state.playersData;
        const players = preparedPlayers.groupedBy.property('team');
        const usedPlayers = this.store.get('usedPlayers', []);

        if (!nonControlled.useStack) {
            return [];
        }

        const createConstraints = function (keyPlayerId, playerIds = [], playersQuantity) {
            const vars = {};
            playerIds.map((playerId) => {
                if (keyPlayerId !== playerId) {
                    vars[getSimplexUnitName(playerId)] = 1;
                }
            });

            if (isEmpty(vars)) {
                return {};
            }

            vars[getSimplexUnitName(keyPlayerId)] = -1 * playersQuantity;

            const ruleBuilder = RuleBuilder.fromAtLeast(0);
            ruleBuilder.addVars(vars);

            return ruleBuilder.createRule();
        };

        const prepareStacks = function (stacksList) {
            let stacks = {...stacksList};
            let preparedStacks = {};
            Object.keys(stacks).map((key) => {
                let stack = {...stacks[key]};
                let groups = Object.values(stack.groups);

                for (let group of groups) {
                    if (!inArray(group.team, playersFilter.games)) {
                        return;
                    }
                }

                stack.players = [];
                stack.desiredPlayersQ = 0;
                groups.map((group) => {
                    if (isEmpty(players[group.team])) {
                        return;
                    }
                    stack.desiredPlayersQ += group.playersQuantity;
                    group.players = [];
                    players[group.team].map((player) => {
                        if (!inArray(player.userLineGroup, group.lineGroups)) {
                            return;
                        }
                        if (!isEmpty(group.ppLines) && !inArray(player.userPpLine, group.ppLines)) {
                            return;
                        }
                        group.players.push(player.playerId);
                    });
                    stack.players = [...new Set([...stack.players, ...group.players])];
                });

                stack.possiblePlayersQ = stack.desiredPlayersQ;
                if (stack.players.length < stack.possiblePlayersQ) {
                    stack.possiblePlayersQ = stack.players.length;
                }

                stack.reservedLineups = calcQuantityOfReservedLineupsByExposure(
                    lineupsQuantity.getLineupsQuantity(),
                    stack.exposure,
                );

                preparedStacks[key] = stack;
            });

            return {...preparedStacks};
        };

        let stacks = this.store.get('nonControlledStack');
        if (null === stacks) {
            stacks = prepareStacks({...nonControlled.list});
        }

        if (!isEmpty(usedPlayers)) {
            let lastLineup = usedPlayers[usedPlayers.length - 1].map(Number);
            Object.values(stacks).map((stack) => {
                if (0 >= stack.reservedLineups) {
                    return;
                }

                let duplicates = arrayDuplicates(lastLineup, stack.players);
                if (stack.possiblePlayersQ <= duplicates.length) {
                    stack.reservedLineups--;
                }
            });
        }

        let stackPlayers = {};
        let stackTeams = {};
        Object.values(stacks).map((stack) => {
            if (0 >= stack.reservedLineups) {
                return;
            }

            Object.values(stack.groups).map((group) => {
                if (isEmpty(group.players)) {
                    return;
                }

                const teamKey = group.players.join('+');

                if (!stackTeams.hasOwnProperty(teamKey)) {
                    stackTeams[teamKey] = {
                        team: group.players.map((playerId) => (team[getSimplexUnitName(playerId)] = 1)),
                        playersQuantity: group.playersQuantity,
                    };
                } else if (group.playersQuantity > stackTeams[teamKey]) {
                    stackTeams[teamKey].playersQuantity = group.playersQuantity;
                }
            });

            stack.players.map((pId) => {
                if (!stackPlayers.hasOwnProperty(pId)) {
                    stackPlayers[pId] = {
                        limit: stack.possiblePlayersQ,
                        players: [],
                    };
                } else if (stack.possiblePlayersQ < stackPlayers[pId].limit) {
                    stackPlayers[pId].limit = stack.possiblePlayersQ;
                }
                stackPlayers[pId].players = [...new Set([...stackPlayers[pId].players, ...stack.players])];
            });
        });

        let result = [];

        if (!isEmpty(stackTeams)) {
            Object.values(stackTeams).map((stackTeam) => {
                const ruleBuilder = RuleBuilder.fromAtMost(stackTeam.playersQuantity);
                ruleBuilder.addVars(stackTeam.team);

                result.push(ruleBuilder.createRule());
            });
        }

        let playerIds = [];
        if (!isEmpty(stackPlayers)) {
            Object.keys(stackPlayers).map((pId) => {
                let stackPlayer = {...stackPlayers[pId]};
                playerIds = [...playerIds, ...stackPlayer.players];
                let arr = [...stackPlayer.players];
                arr.splice(arr.indexOf(Number(pId)), 1);

                result.push(createConstraints(pId, arr, stackPlayer.limit - 1));
            });
        }

        if (!isEmpty(playerIds)) {
            const ruleBuilder = RuleBuilder.fromAtLeast(1);
            [...new Set(playerIds)].map((playerId) => {
                ruleBuilder.addVar(getSimplexUnitName(playerId), 1);
            });

            result.push(ruleBuilder.createRule());
        }

        this.store.set('nonControlledStack', stacks);
        // console.log("nonControlledStack\n",result);
        return result;
    }

    singlegameForcedStacking({incomingValues: {}}) {
        const {
            singlegameForcedStacking: {settings: stacking},
            lineupsQuantity,
            modesConfig,
            playersData: {preparedPlayers},
        } = Action.getState();

        const rulesBuilder = SinglegameForcedStackingRulesBuilderFactory.getBuilder({
            stacking,
            groupedPlayersByTeams: preparedPlayers.groupedBy.property('team'),
        });

        if (!stacking.isEnabled()) {
            return [];
        }

        const buildCombinationRules = function () {
            let rules = this.store.get('singlegameForcedStacking.rules', {});
            if (isEmpty(rules)) {
                rules = rulesBuilder.createCombinationsRules({modesConfig});

                this.store.set('singlegameForcedStacking.rules', {...rules});
            }

            return {...rules};
        }.bind(this);

        const buildCombinationsQuantity = function () {
            let combinationsQuantity = this.store.get('singlegameForcedStacking.combinationsQuantity', []);
            if (isEmpty(combinationsQuantity)) {
                combinationsQuantity = rulesBuilder.buildCombinationsQuantity({lineupsQuantity});

                this.store.set('singlegameForcedStacking.combinationsQuantity', [...combinationsQuantity]);
            }

            return [...combinationsQuantity];
        }.bind(this);

        const chooseCombinationRule = function (comboRules) {
            const combinationsQuantity = buildCombinationsQuantity();

            combinationsQuantity.sort(sortByProperty('lineupsQuantity', 'desc'));
            const combinationQuantity = combinationsQuantity[0];

            let comboRule = {};
            if (0 < combinationQuantity.lineupsQuantity) {
                comboRule = comboRules[combinationQuantity.comboKey];
                combinationQuantity.lineupsQuantity--;
            }

            return {...comboRule};
        }.bind(this);

        const comboRules = buildCombinationRules();
        // console.log('comboRules',{...comboRules});
        const chosenRule = chooseCombinationRule(comboRules);
        // console.log('chosenRule',{...chosenRule});
        if (isEmpty(chosenRule)) {
            return [];
        }

        const generalObjective = {};
        chosenRule.groupNames.map((groupName) => (generalObjective[groupName] = 0));

        this.store.patchObj('additionalObjective', {
            'singlegameForcedStacking.generalObjective': {...generalObjective},
        });

        const rules = [...chosenRule.rules];
        // console.log("singlegameForcedStacking\n",rules);
        return rules;
    }

    multiPositions({incomingValues = {}}) {
        const {
            playersLock,
            playersData: {preparedPlayers},
        } = Action.getState();
        const players = preparedPlayers.groupedBy.doublePosition();

        const rules = [];

        if (isEmpty(players)) {
            return rules;
        }

        players.map((player) => {
            const ruleBuilder = new RuleBuilder();
            player.position.map((positionKey) => {
                ruleBuilder.addVar(getSimplexUnitName([player.playerId, positionKey]), 1);
            });

            if (playersLock.isLockedAnywhere(player.getId())) {
                ruleBuilder.exactly(1);
            } else {
                ruleBuilder.atMost(1);
            }

            rules.push(ruleBuilder.createRule());
        });
        // console.log('multiPositions',rules);
        return [...rules];
    }

    createdLineups({incomingValues: {checkIfPlayerOnDoublePosition = false}}) {
        const {
            sportConfig,
            uniquePlayersQuantity,
            lineupGenerator: {settings},
            playersData: {preparedPlayers},
        } = Action.getState();
        const playersIndexedById = preparedPlayers.groupedBy.playerId();

        let playersQuantity = uniquePlayersQuantity.getUniquePlayersQuantity();
        if (settings.isRelaxBuildRulesUsed()) {
            playersQuantity = Const.DEFAULT_UNIQUE_PLAYERS_QUANTITY;
        }

        const atMostValue = sportConfig.getConfigProp('playersInTeam') - playersQuantity;

        const rules = [];
        this.store.get('usedPlayers', []).map((lineup) => {
            const ruleBuilder = RuleBuilder.fromAtMost(atMostValue);

            lineup.map((playerId) => {
                const simplexUnitNames = getSimplexUnitNamesByParams({
                    playerId,
                    playersIndexedById,
                    checkIfPlayerOnDoublePosition,
                });
                simplexUnitNames.map((simplexUnitName) => {
                    ruleBuilder.addVar(simplexUnitName, 1);
                });
            });

            if (ruleBuilder.isRuleCreatable()) {
                rules.push(ruleBuilder.createRule());
            }
        });

        return [...rules];
    }

    exposureMax({incomingValues: {checkIfPlayerOnDoublePosition = false}}) {
        const {
            playersData: {preparedPlayers},
            exposureMode,
            lineupsQuantity,
            lineupGenerator: {settings},
        } = Action.getState();

        if (settings.isRelaxBuildRulesUsed()) {
            return [];
        }

        const players = preparedPlayers.groupedBy.property('exposureMax');
        const denyConstraints = this.store.get('denyConstraints', []);

        const rules = [];

        if (isEmpty(players) || inArray('exposureMax', denyConstraints)) {
            return rules;
        }

        const playersIndexedById = preparedPlayers.groupedBy.playerId();
        const lineups = this.store.get('lineups', []);
        let forbiddenPlayerIds = [];

        function isPlayerForbidden(player, lineupsQuantity) {
            const playerUIds = getPlayerUIdsByParams({
                playerId: player.playerId,
                playersIndexedById,
                checkIfPlayerOnDoublePosition,
            });

            let usedInLineups = 0;
            playerUIds.map((playerUId) => {
                usedInLineups += countPlayerIdInLineups(playerUId, lineups.join(''));
            });

            const usedInLineupsPercentage = ((usedInLineups + 1) / lineupsQuantity) * 100;

            return usedInLineupsPercentage > player.exposureMax;
        }

        const ruleBuilder = RuleBuilder.fromAtMost(0);

        if (exposureMode.isModeTournament()) {
            if (lineups.length) {
                const lineupsQuantity = 1 + lineups.length;

                Object.keys(players).map((exp) => {
                    players[exp]
                        .filter((player) => isPlayerForbidden(player, lineupsQuantity))
                        .map((player) => forbiddenPlayerIds.push(player.playerId));
                });
            }
        } else {
            Object.keys(players).map((exp) => {
                players[exp]
                    .filter((player) => isPlayerForbidden(player, lineupsQuantity.getLineupsQuantity()))
                    .map((player) => forbiddenPlayerIds.push(player.playerId));
            });
        }
        // console.log('forbiddenPlayerIds',forbiddenPlayerIds);
        forbiddenPlayerIds.map((forbiddenPlayerId) => {
            const simplexUnitNames = getSimplexUnitNamesByParams({
                playerId: forbiddenPlayerId,
                playersIndexedById,
                checkIfPlayerOnDoublePosition,
            });

            simplexUnitNames.map((simplexUnitName) => ruleBuilder.addVar(simplexUnitName, 1));
        });

        if (ruleBuilder.isRuleCreatable()) {
            rules.push(ruleBuilder.createRule());
        }

        this.store.set('playersForbiddenByExposureMax', forbiddenPlayerIds);

        return [...rules];
    }

    exposureMin({incomingValues: {checkIfPlayerOnDoublePosition = false}}) {
        const denyConstraints = this.store.get('denyConstraints', []);

        let rules = [];
        if (inArray('exposureMin', denyConstraints)) {
            return rules;
        }

        const {
            sportConfig,
            lineupsQuantity,
            playersData: {preparedPlayers},
            lineupGenerator: {settings},
        } = Action.getState();
        const players = preparedPlayers.groupedBy.property('exposureMin');

        if (settings.isRelaxBuildRulesUsed() || isEmpty(players)) {
            return rules;
        }

        /**
         * The main idea in order to solve the exposureMin problem contains two points.
         *
         * The first is:
         * to build 'exposure line' that will make the GLPK library use players from it.
         * Sample, if we need to use players x111 and x222 in lineups,
         * then the 'exposure line' will look like +x111+x222.
         * After that we need to define a coefficient.
         * Assume the player x111 has exposureMin value equal 0.4 and another one x222 has 0.7.
         * in this case coefficient will be next: 0.4 + 0.7 = 1.1
         * The next thing we need to do round up given coefficient - Math.ceil(1.1) = 2;
         * As a result full exposure line is: +x111+x222 >= 2;
         *
         * The second point is:
         * to set hard constraints for players which have to be in each residual lineups necessarily.
         * Sample, if player x111 have to be in each lineup,
         * expression will be: +x111 = 1;
         */

        const forbiddenPlayers = this.store.get('playersForbiddenByExposureMax', []);
        const playersIndexedById = preparedPlayers.groupedBy.playerId();
        const lineups = this.store.get('lineups', []);
        // Q means 'quantity';
        // residualLineupsQ - quantity lineups that should be calculated.
        const residualLineupsQ = lineupsQuantity.getLineupsQuantity() - this.store.get('calculatedLineupsCount', 0);
        let amountCoefficients = 0;
        const positionsCoefficient = {};
        const softRuleVars = {};

        Object.keys(players).map((exp) => {
            players[exp].map((player) => {
                if (inArray(player.playerId, forbiddenPlayers)) {
                    return;
                }

                const playerUIds = getPlayerUIdsByParams({
                    playerId: player.playerId,
                    playersIndexedById,
                    checkIfPlayerOnDoublePosition,
                });

                let usedInLineups = 0;
                playerUIds.map((playerUId) => {
                    usedInLineups += countPlayerIdInLineups(playerUId, lineups.join(''));
                });

                // reservedLineupsQ - quantity reserved lineups by one player.
                // For instance if 'lineupsCount' was set in 100
                // and some player has 'exposureMin' equal 20 then reservedLineupsQ will be 20.
                let reservedLineupsQ = calcQuantityOfReservedLineupsByExposure(
                    lineupsQuantity.getLineupsQuantity(),
                    player.exposureMin,
                );

                // residualReservedLineupsQ - quantity of residual reserved lineups.
                // For instance if common value of reserved lineups is 10
                // and a player has already set in 2 lineups then 10 - 2 = 8
                let residualReservedLineupsQ = reservedLineupsQ - usedInLineups;

                // If residualReservedLineupsQ is 0, move to the next iteration.
                // It means there is no necessary to add him in the lineup.
                if (residualReservedLineupsQ > 0) {
                    const simplexUnitNames = getSimplexUnitNamesByParams({
                        playerId: player.playerId,
                        playersIndexedById,
                        checkIfPlayerOnDoublePosition,
                    });

                    // If residualReservedLineupsQ equal residualLineupsQ,
                    // set hard constraints for this player.
                    // Need to specify that this player has to be in each next lineups.
                    if (residualReservedLineupsQ >= residualLineupsQ) {
                        const strictRuleBuilder = RuleBuilder.fromExactly(1);
                        simplexUnitNames.map((simplexUnitName) => strictRuleBuilder.addVar(simplexUnitName, 1));

                        rules.push(strictRuleBuilder.createRule());
                    } else {
                        // Calculate coefficient reserved places in one lineup.
                        // For instance if the first player has value 0.4,
                        // the second one 0.7 and the third one 0.1, then 0.4 + 0.7 + 0.1 = 1.3
                        let coefficientReservedPlaces = residualReservedLineupsQ / residualLineupsQ;

                        // Shared coefficient by positions
                        // in order to verify that maxPlayers from config is not exceeded.
                        player.position.map((pos) => {
                            if (!positionsCoefficient.hasOwnProperty(pos)) {
                                positionsCoefficient[pos] = 0;
                            }
                            if (isPlayerOnDoublePosition(player)) {
                                coefficientReservedPlaces = coefficientReservedPlaces / 2;
                            }
                            positionsCoefficient[pos] += coefficientReservedPlaces;
                        });

                        // Build line that makes the GLPK use these players during a creation of a lineup.
                        // For instance: +x111+x222+x333
                        simplexUnitNames.map((simplexUnitName) => (softRuleVars[simplexUnitName] = 1));
                    }
                }
            });
        });

        // If coefficient on one position is exceeded, set value from config.
        const positions = sportConfig.getConfigProp('positions');
        Object.keys(positionsCoefficient).map((pos) => {
            let coef = positionsCoefficient[pos];
            if (positions.hasOwnProperty(pos)) {
                const position = sportConfig.getJointPosition(pos) || pos;
                const maxPlayers = Number(sportConfig.getMaxPlayersOnPosition(position));
                if (maxPlayers && coef > maxPlayers) {
                    coef = maxPlayers;
                }
            }
            amountCoefficients += coef;
        });

        // If amountCoefficients is more than 0,
        // set exposure line with a list of players which have to be in lineups.
        if (amountCoefficients > 0 && !isEmpty(softRuleVars)) {
            amountCoefficients = Math.ceil(amountCoefficients);
            if (amountCoefficients > sportConfig.getPlayersInTeam()) {
                amountCoefficients = sportConfig.getPlayersInTeam();
            }
            // For instance: +x111+x222+x333 >= 2
            const softRuleBuilder = RuleBuilder.fromAtLeast(amountCoefficients);
            softRuleBuilder.addVars(softRuleVars);

            rules.push(softRuleBuilder.createRule());
        }
        // console.log("exposureMin\n",rules);
        return [...rules];
    }

    additionalObjective({incomingValues = {}}) {
        let result = {};

        /**
         * Extra objective items which can be added during forming of constraints.
         * E.g. non controlled hitters stack.
         */
        const additionalObjective = this.store.get('additionalObjective', {});
        // console.log('additionalObjective',{...additionalObjective});
        if (!isEmpty(additionalObjective)) {
            Object.values(additionalObjective).map((item) => {
                result = {...result, ...item};
            });
        }
        // console.log('additionalObjective',{...result});
        return {...result};
    }

    flexMaxExposure({incomingValues: {unitIdPropName = 'playerId', flexPositionsQuantity = 1}}) {
        const {
            sportConfig,
            playersData: {preparedPlayers},
        } = Action.getState();
        const positions = sportConfig.getConfigProp('positions', {});
        const forbiddenFlexPositions = getLpService(sportConfig, {store: this.store}).exec(
            'getForbiddenFlexMaxExposurePositions',
            {flexPositionsQuantity},
        );
        const players = preparedPlayers.groupedBy.allowedPositions();

        const rules = [];
        Object.keys(positions).map((positionKey) => {
            const ruleVars = {};
            const addedPlayers = [];
            const position = positions[positionKey];
            const isPositionFlex = position.id === 'flex';

            players.map((player) => {
                if (player.position != positionKey || inArray(player[unitIdPropName], addedPlayers)) {
                    return;
                }

                ruleVars[getSimplexUnitName(player[unitIdPropName])] = 1;

                addedPlayers.push(player[unitIdPropName]);
            });

            if (isEmpty(ruleVars)) {
                return;
            }

            let {maxPlayers, minPlayers} = position;
            if (!isPositionFlex && !isEmpty(forbiddenFlexPositions)) {
                if (inArray(position.shortName, Object.keys(forbiddenFlexPositions))) {
                    maxPlayers -= forbiddenFlexPositions[position.shortName];
                }
            }

            if (maxPlayers == minPlayers) {
                const minPlayersRuleBuilder = RuleBuilder.fromExactly(maxPlayers);
                minPlayersRuleBuilder.addVars({...ruleVars});

                rules.push(minPlayersRuleBuilder.createRule());
            } else {
                const minPlayersRuleBuilder = RuleBuilder.fromAtLeast(minPlayers);
                minPlayersRuleBuilder.addVars({...ruleVars});

                rules.push(minPlayersRuleBuilder.createRule());

                const maxPlayersRuleBuilder = RuleBuilder.fromAtMost(maxPlayers);
                maxPlayersRuleBuilder.addVars({...ruleVars});

                rules.push(maxPlayersRuleBuilder.createRule());
            }
        });
        // console.log("flexMaxExposure\n",rules);
        return [...rules];
    }

    stackQuarterbackWithPassReceivers({incomingValues = {}}) {
        const {
            playersData: {preparedPlayers},
            lineupsData: {
                filters: {stackQuarterbackWithPassReceivers},
            },
        } = Action.getState();

        const rules = [];

        if (!stackQuarterbackWithPassReceivers) {
            return rules;
        }

        const teams = preparedPlayers.groupedBy.teamAndPosition();

        Object.keys(teams).map((key) => {
            const team = teams[key].positions;
            const passReceivers = [];
            const quarterbacks = [];
            const qbPositionName = 'QB';

            if (!(qbPositionName in team)) {
                return;
            }

            Const.NFL_PASS_RECEIVER_POSITIONS.map((pos) => {
                if (pos in team) {
                    team[pos].map((player) => {
                        passReceivers.push(
                            addRoleSuffix({
                                prefix: getSimplexUnitName(player.playerId),
                                role: Const.FLEX_MODE,
                            }),
                        );
                    });
                }
            });

            team[qbPositionName].map((player) => {
                quarterbacks.push(
                    addRoleSuffix({
                        prefix: getSimplexUnitName(player.playerId),
                        role: Const.CAPTAIN_MODE,
                    }),
                );
            });

            if (isEmpty(passReceivers)) {
                const builder = RuleBuilder.fromExactly(0);

                quarterbacks.map((simplexUnitName) => builder.addVar(simplexUnitName, 1));

                rules.push(builder.createRule());
            } else {
                const builder = RuleBuilder.fromAtLeast(0);

                passReceivers.map((simplexUnitName) => builder.addVar(simplexUnitName, 1));
                quarterbacks.map((simplexUnitName) => builder.addVar(simplexUnitName, -1));

                rules.push(builder.createRule());
            }
        });
        // console.log("stackQuarterbackWithPassReceivers\n", rules);
        return [...rules];
    }

    passReceiver({incomingValues: {checkIfPlayerOnDoublePosition = false}}) {
        const {
            sportConfig,
            playersData: {preparedPlayers},
            lineupsData: {
                filters: {avoidFewPassReceivers},
            },
            lineupGenerator: {settings},
        } = Action.getState();

        const rules = [];

        if (settings.isRelaxBuildRulesUsed() || !avoidFewPassReceivers) {
            return rules;
        }

        const teams = preparedPlayers.groupedBy.teamAndPosition();
        const players = preparedPlayers.groupedBy.playerId();

        function isPlayerOnSinglePosition(playerId) {
            if (!checkIfPlayerOnDoublePosition) {
                return true;
            }
            return !(players.hasOwnProperty(playerId) && isPlayerOnDoublePosition(players[playerId]));
        }

        function getSimplexUnitNameByPlayerIdAndPositionName(playerId, positionName) {
            if (isPlayerOnSinglePosition(playerId)) {
                return getSimplexUnitName(playerId);
            }

            const position = sportConfig.getPositionByName(positionName);

            if (isEmpty(position)) {
                return getSimplexUnitName(playerId);
            }

            const [positionKey] = position.allowedPositions;

            if (!positionKey) {
                return getSimplexUnitName(playerId);
            }

            return getSimplexUnitName([playerId, positionKey]);
        }

        Object.keys(teams).map((key) => {
            let team = teams[key].positions;
            let passReceivers = [];
            let row = {};

            Const.NFL_PASS_RECEIVER_POSITIONS.map((pos) => {
                if (team.hasOwnProperty(pos)) {
                    team[pos].map((player) => {
                        passReceivers.push(getSimplexUnitNameByPlayerIdAndPositionName(player.playerId, pos));
                    });
                }
            });

            if (isEmpty(passReceivers)) {
                return;
            }

            passReceivers.map((simplexUnitName) => (row[simplexUnitName] = 1));

            const qbPositionName = 'QB';
            if (team.hasOwnProperty(qbPositionName)) {
                team[qbPositionName].map((player) => {
                    const simplexUnitName = getSimplexUnitNameByPlayerIdAndPositionName(
                        player.playerId,
                        qbPositionName,
                    );
                    row[simplexUnitName] = -1 * (passReceivers.length - 1);
                });
            }

            rules.push({
                row: {...row},
                type: Const.STACKS_NUMBER_TYPE_AT_MOST,
                obj: 1,
            });
        });
        // console.log("passReceiver\n", rules);
        return [...rules];
    }

    opposingTeamsInLineup({incomingValues: {modes, allowOpposingTeamsInLineup = null}}) {
        const state = Action.getState();

        if (null === allowOpposingTeamsInLineup) {
            const {filters: lineupsFilter} = state.lineupsData;
            allowOpposingTeamsInLineup = lineupsFilter.allowOpposingTeamsInLineup;
        }

        if (allowOpposingTeamsInLineup) {
            return [];
        }

        const {preparedPlayers} = state.playersData;
        const playersByGames = preparedPlayers.groupedBy.property('gameInfo');

        const constraints = [];
        const objective = {};
        Object.values(playersByGames).map((players) => {
            const playersByTeams = distributePlayersByTeams(players);

            const lpGameTeamsConstraints = createConstraintsForGameTeams(playersByTeams);

            const lpGameTeams = Object.keys(lpGameTeamsConstraints);
            if (!isBothTeamsTakePart(lpGameTeams)) {
                return;
            }

            const builder = RuleBuilder.fromAtMost(1);

            lpGameTeams.map((lpTeam) => {
                builder.addVar(lpTeam, 1);
                constraints.push(lpGameTeamsConstraints[lpTeam]);
                objective[lpTeam] = 0;
            });

            constraints.push(builder.createRule());
        });

        function distributePlayersByTeams(players) {
            const playersByTeams = {};
            players.map((player) => {
                const teamName = normalizeTeamName(player.team);
                if (!playersByTeams.hasOwnProperty(teamName)) {
                    playersByTeams[teamName] = [];
                }
                playersByTeams[teamName].push(player);
            });
            return playersByTeams;
        }

        function normalizeTeamName(teamName) {
            return teamName.replace(/[\s\-]/u, '');
        }

        function createConstraintsForGameTeams(playersByTeams) {
            const lpGameTeamsConstraints = {};
            Object.keys(playersByTeams).map((teamName) => {
                if (isEmpty(playersByTeams[teamName])) {
                    return;
                }

                const builder = RuleBuilder.fromAtMost(0);

                let teamPlayersQuantity = 0;
                playersByTeams[teamName].map((player) => {
                    modes.map((playerMode) => {
                        teamPlayersQuantity++;
                        const lpPlayerId = addRoleSuffix({
                            prefix: player.getId(),
                            role: playerMode,
                        });
                        builder.addVar(getSimplexUnitName(lpPlayerId), 1);
                    });
                });

                const lpTeam = Const.LP_TEAM_GROUP_PREFIX + normalizeTeamName(teamName);

                builder.addVar(lpTeam, -1 * teamPlayersQuantity);
                lpGameTeamsConstraints[lpTeam] = builder.createRule();
            });
            return lpGameTeamsConstraints;
        }

        function isBothTeamsTakePart(teams) {
            return 2 === teams.length;
        }

        this.store.patchObj('additionalObjective', {
            opposingTeamsInLineup: {...objective},
        });

        return [...constraints];
    }

    opposingOpponentsInLineup({incomingValues: {modes}}) {
        const {
            playersData: {preparedPlayers},
            competitionStacking,
            lineupsQuantity,
        } = Action.getState();

        const competitions = preparedPlayers.groupedBy.property('competitionName');

        const getStackedCompetitions = function () {
            const groupsOfCompetitions = this.store.get('usedTeamGroups', []);
            const stackedCompetitions = [];
            groupsOfCompetitions.map((group) => {
                const usedMarkedCompetitions = CompetitionStackMarker.pickMarkedBySuffix(group);
                usedMarkedCompetitions.map((item) => {
                    stackedCompetitions.push(item);
                });
            });
            return stackedCompetitions;
        }.bind(this);

        const getSettingsState = function () {
            let settingsState = this.store.get('opponentsStackSettingsState', {});

            if (!isEmpty(settingsState)) {
                return settingsState;
            }

            const quantity = lineupsQuantity.getLineupsQuantity();
            Object.values(competitionStacking.getItems()).map((stackItem) => {
                const simplexKey = createSimplexCompetitionKey(stackItem.getOpponentIds());
                settingsState[simplexKey] = {
                    reservedLineups: calcAndRoundUpValByPct(quantity, stackItem.getExposure()),
                };
            });

            this.store.set('opponentsStackSettingsState', settingsState);

            return settingsState;
        }.bind(this);

        function shouldOpponentsBeStacked(opponentIds) {
            const stackedCompetitions = getStackedCompetitions();
            const settingsState = getSettingsState();

            const simplexKey = createSimplexCompetitionKey(opponentIds);

            const reservedLineups = settingsState[simplexKey].reservedLineups;
            const entriesQuantity = countEntries(simplexKey, stackedCompetitions);

            return entriesQuantity < reservedLineups;
        }

        function divideStackableCompetitions(competitions) {
            const stackableCompetitions = {},
                notStackableCompetitions = {},
                stackedCompetitions = {};

            if (!competitionStacking.isEnabled()) {
                return {
                    notStackableCompetitions: competitions,
                    stackableCompetitions: stackableCompetitions,
                    stackedCompetitions: stackedCompetitions,
                };
            }

            Object.keys(competitions).map((competitionId) => {
                const opponentIds = competitionStacking.getOpponentIdsByCompetitionId(competitionId);

                if (isEmpty(opponentIds)) {
                    notStackableCompetitions[competitionId] = [...competitions[competitionId]];
                    return;
                }

                if (shouldOpponentsBeStacked(opponentIds)) {
                    stackableCompetitions[competitionId] = [...competitions[competitionId]];
                } else {
                    stackedCompetitions[competitionId] = [...competitions[competitionId]];
                }
            });

            // Stacked competitions get available for stacking again if slates are exhausted by exposure
            if (isEmpty(stackableCompetitions)) {
                return {
                    stackableCompetitions: stackableCompetitions,
                    notStackableCompetitions: notStackableCompetitions,
                    stackedCompetitions: stackedCompetitions,
                };
            }

            return {
                stackableCompetitions,
                notStackableCompetitions: {
                    ...notStackableCompetitions,
                    ...stackedCompetitions,
                },
                stackedCompetitions: {},
            };
        }

        const {stackableCompetitions, notStackableCompetitions, stackedCompetitions} =
            divideStackableCompetitions(competitions);

        const ConstraintsBuilder = CompetitionStackingConstraintsBuilderFactory.getBuilder();
        const constraintsBuilder = new ConstraintsBuilder({modes});

        const notStackableOpponentsConstraint = constraintsBuilder.createConstraintForNotStackableOpponents({
            competitions: notStackableCompetitions,
        });

        const stackableOpponentsConstraint = constraintsBuilder.createConstraintForStackableOpponents({
            competitions: stackableCompetitions,
        });

        // const stackedOpponentsConstraint = constraintsBuilder.createConstraintForStackedOpponents({
        //     competitions: stackedCompetitions,
        // });
        // Fix: #22966
        const stackedOpponentsConstraint = constraintsBuilder.createConstraintForNotStackableOpponents({
            competitions: stackedCompetitions,
        });

        const simplexKeys = [
            ...notStackableOpponentsConstraint.simplexKeys,
            ...stackableOpponentsConstraint.simplexKeys,
            ...stackedOpponentsConstraint.simplexKeys,
        ];

        const objective = {};
        simplexKeys.map((simplexKey) => (objective[simplexKey] = 0));

        const constraints = [
            ...notStackableOpponentsConstraint.constraints,
            ...stackableOpponentsConstraint.constraints,
            ...stackedOpponentsConstraint.constraints,
        ];

        this.store.patchObj('additionalObjective', {
            opposingOpponentsInLineup: {...objective},
        });
        // console.log('constraints',[...constraints]);
        return [...constraints];
    }

    // opposingFightersInLineup_v1({incomingValues: {modes}}) {
    //
    //     const {playersData: {preparedPlayers}, fightStacking} = Action.getState();
    //     const fights = preparedPlayers.groupedBy.property('gameInfo');
    //
    //     const constraints = [];
    //     const objective = {};
    //
    //     {
    //         const notStackableFights = getNotStackableFights(fights);
    //         Object.values(notStackableFights).map(fighters => {
    //
    //             const fightersConstraints = createConstraintsForFighters(fighters);
    //             const fightersConstraintKeys = Object.keys(fightersConstraints);
    //
    //             if (isOneFighterTakesPart(fightersConstraintKeys)) {
    //                 return;
    //             }
    //
    //             const builder = new RuleBuilder;
    //             builder.atMost(1);
    //
    //             fightersConstraintKeys.map(lpTeam => {
    //                 builder.addVar(lpTeam, 1);
    //                 constraints.push(fightersConstraints[lpTeam]);
    //                 objective[lpTeam] = 0;
    //             });
    //
    //             constraints.push(builder.createRule());
    //         });
    //     }
    //
    //     {
    //         if (fightStacking.isEnabled()) {
    //             const builder = new RuleBuilder;
    //             builder.atMost(2 * fightStacking.getAllowedFighterVsOpponentQuantity());
    //
    //             const stackableFights = getStackableFights(fights);
    //             Object.values(stackableFights).map(fighters => {
    //
    //                 const fightersConstraints = createConstraintsForFighters(fighters);
    //                 const fightersConstraintKeys = Object.keys(fightersConstraints);
    //
    //                 if (isOneFighterTakesPart(fightersConstraintKeys)) {
    //                     return;
    //                 }
    //
    //                 fightersConstraintKeys.map(lpTeam => {
    //                     builder.addVar(lpTeam, 1);
    //                     constraints.push(fightersConstraints[lpTeam]);
    //                     objective[lpTeam] = 0;
    //                 });
    //             });
    //
    //             constraints.push(builder.createRule());
    //         }
    //     }
    //
    //     function getNotStackableFights(fights) {
    //         const filteredFights = {};
    //         Object.keys(fights).map(key => {
    //             if (!fightStacking.hasFightById(key)) {
    //                 filteredFights[key] = [...fights[key]];
    //             }
    //         });
    //         return filteredFights;
    //     }
    //
    //     function getStackableFights(fights) {
    //         const filteredFights = {};
    //         Object.keys(fights).map(key => {
    //             if (fightStacking.hasFightById(key)) {
    //                 filteredFights[key] = [...fights[key]];
    //             }
    //         });
    //         return filteredFights;
    //     }
    //
    //     function mapFighters(fighters) {
    //         const mappedFighters = {};
    //         fighters.map(fighter => {
    //             const teamName = normalizeTeamName(fighter.team);
    //             if (!mappedFighters.hasOwnProperty(teamName)) {
    //                 mappedFighters[teamName] = [];
    //             }
    //             mappedFighters[teamName].push(fighter);
    //         });
    //         return mappedFighters;
    //     }
    //
    //     function normalizeTeamName(teamName) {
    //         return teamName.replace(/[\s\-]/u, '');
    //     }
    //
    //     function createConstraintsForFighters(fighters) {
    //         const mappedFighters = mapFighters(fighters);
    //         const constraints = {};
    //         Object.keys(mappedFighters).map(teamName => {
    //             if (isEmpty(mappedFighters[teamName])) {
    //                 return;
    //             }
    //
    //             const builder = new RuleBuilder;
    //             builder.atMost(0);
    //
    //             let teamPlayersQuantity = 0;
    //             mappedFighters[teamName].map(player => {
    //                 modes.map(playerMode => {
    //                     teamPlayersQuantity++;
    //                     const lpPlayerId = addRoleSuffix({
    //                         prefix: player.getId(),
    //                         role: playerMode
    //                     });
    //                     builder.addVar(Const.LP_UNIT_PREFIX + lpPlayerId, 1);
    //                 });
    //             });
    //
    //             const lpTeam = Const.LP_TEAM_GROUP_PREFIX + normalizeTeamName(teamName);
    //
    //             builder.addVar(lpTeam, -1 * teamPlayersQuantity);
    //             constraints[lpTeam] = builder.createRule();
    //         });
    //         return constraints;
    //     }
    //
    //     function isOneFighterTakesPart(fighters) {
    //         return 2 > fighters.length;
    //     }
    //
    //     this.store.patchObj('additionalObjective', {
    //         opposingTeamsInLineup: {...objective}
    //     });
    //
    //     return [...constraints];
    // }

    bound({incomingValues: {checkIfPlayerOnDoublePosition = false}}) {
        const {
            playersLock,
            playersData: {preparedPlayers},
        } = Action.getState();
        const playersIndexedById = preparedPlayers.groupedBy.playerId();

        let rules = [];
        Object.values(playersIndexedById).map((player) => {
            if (!playersLock.isLockedAnywhere(player.getId())) {
                return;
            }

            const ruleBuilder = RuleBuilder.fromExactly(1);

            const simplexUnitNames = getSimplexUnitNamesByParams({
                playerId: player.getId(),
                playersIndexedById,
                checkIfPlayerOnDoublePosition,
            });

            simplexUnitNames.map((simplexUnitName) => {
                ruleBuilder.addVar(simplexUnitName, 1);
            });

            rules.push(ruleBuilder.createRule());
        });

        return [...rules];
    }

    teamExposureMax({incomingValues: {minPlayersQuantityToIdentifyUsedTeam = 1}}) {
        const state = Action.getState();
        const {
            sportConfig,
            exposureMode,
            lineupsQuantity,
            usedTeams: {exposureMax},
            lineupsData: {rawLineups},
            playersData: {preparedPlayers},
        } = state;
        const teamsExposureMax = exposureMax.all();
        const playersByTeams = preparedPlayers.groupedBy.property('team');

        if (isEmpty(teamsExposureMax)) {
            return [];
        }

        const getTeamsUsedInLineups = function () {
            const teamsUsedInLineups = this.store.get('teamsUsedInLineups', {});
            const lastLineup = rawLineups.getLast();

            if (!isEmpty(lastLineup) && !teamsUsedInLineups.hasOwnProperty(lastLineup.getId())) {
                const LineupAnalyzer = LineupAnalyzerFactory.getAnalyzer(sportConfig);
                const teamNames = new LineupAnalyzer(rawLineups.getLast()).getTeamNamesWithAtLeastNPlayers(
                    minPlayersQuantityToIdentifyUsedTeam,
                );
                teamsUsedInLineups[lastLineup.getId()] = teamNames;

                this.store.set('teamsUsedInLineups', {...teamsUsedInLineups});
            }

            return Object.values(teamsUsedInLineups);
        }.bind(this);

        const result = [];

        const teamsUsedInLineups = getTeamsUsedInLineups();
        const limitPlayersFromOneTeam = minPlayersQuantityToIdentifyUsedTeam - 1;
        const builder = RuleBuilder.fromAtMost(limitPlayersFromOneTeam);

        if (exposureMode.isModeTournament()) {
            if (rawLineups.getQuantity()) {
                const mergedTeamNames = mergeMultidimensionalArr(teamsUsedInLineups);
                Object.keys(teamsExposureMax).map((teamName) => {
                    const expMax = teamsExposureMax[teamName];
                    const usageQuantity = countEntries(teamName, mergedTeamNames);
                    const usagePercent = ((usageQuantity + 1) / (1 + lineupsQuantity.getLineupsQuantity())) * 100;

                    if (usagePercent > expMax) {
                        playersByTeams[teamName].map((player) => {
                            builder.addVar(getSimplexUnitName(player.getId()), 1);
                        });
                    }
                });
            }
        } else {
            const mergedTeamNames = mergeMultidimensionalArr(teamsUsedInLineups);

            Object.keys(teamsExposureMax).map((teamName) => {
                const expMax = teamsExposureMax[teamName];
                const usageQuantity = countEntries(teamName, mergedTeamNames);
                const usagePercent = ((usageQuantity + 1) / lineupsQuantity.getLineupsQuantity()) * 100;

                if (usagePercent > expMax) {
                    playersByTeams[teamName].map((player) => {
                        builder.addVar(getSimplexUnitName(player.getId()), 1);
                    });
                }
            });
        }

        if (builder.isRuleCreatable()) {
            result.push(builder.createRule());
        }

        return [...result];
    }

    playerCombinationStacking({incomingValues: {checkIfPlayerOnDoublePosition = false, modes = []}}) {
        const {
            playerCombinationStacking: stacking,
            playersData: {preparedPlayers},
            lineupsData: {rawLineups},
            lineupGenerator: {settings},
        } = Action.getState();

        if (settings.isRelaxBuildRulesUsed() || !stacking.isEnabled() || isEmpty(stacking.getItems())) {
            return [];
        }

        const constraintsBuilder = PlayerCombinationStackingConstrainsBuilderFactory.getBuilder();
        constraintsBuilder.setDependencies({
            checkIfPlayerOnDoublePosition,
            modes,
            players: preparedPlayers.groupedBy.playerId(),
        });

        function createLockedConstraintsState(stackItems) {
            const constraintState = {
                constraints: [],
                objective: {},
            };
            const usedStackItems = [];
            Object.values(stackItems).map((stackItem) => {
                if (!stackItem.isStackUsedInAllLineups()) {
                    return;
                }

                const stackItemConstraints = constraintsBuilder.createStackItemConstraints(stackItem);

                if (isEmpty(stackItemConstraints)) {
                    return;
                }

                constraintState.constraints.push(...stackItemConstraints);
                constraintState.constraints.push(constraintsBuilder.createLockedKeyUnitConstraint(stackItem));

                usedStackItems.push(stackItem);
            });

            if (!isEmpty(usedStackItems)) {
                const keyUnitPoints = 0;
                constraintState.objective = constraintsBuilder.createObjective(usedStackItems, keyUnitPoints);
            }

            return constraintState;
        }

        const createLockedConstraints = function () {
            const constraintsState = this.store.get('pсs.lockedConstraintsState', {
                constraints: [],
                objective: {},
                isCreated: false,
            });

            if (!constraintsState.isCreated) {
                const {constraints, objective} = createLockedConstraintsState(stacking.getItems());

                constraintsState.constraints = constraints;
                constraintsState.objective = objective;
                constraintsState.isCreated = true;

                this.store.set('pсs.lockedConstraintsState', constraintsState);
            }

            const {constraints, objective} = constraintsState;

            return {constraints, objective};
        }.bind(this);

        function createLimitedConstraintsState(stackItems) {
            const state = {};
            Object.values(stackItems).map((stackItem) => {
                if (stackItem.isStackUsedInAllLineups()) {
                    return;
                }

                const stackItemConstraints = constraintsBuilder.createStackItemConstraints(stackItem);

                if (isEmpty(stackItemConstraints)) {
                    return;
                }

                state[stackItem.getId()] = {
                    constraints: stackItemConstraints,
                    lineupsQuantity: stackItem.getMinLineups(),
                    stackItem,
                };
            });

            return state;
        }

        function setStackUsageInLimitedConstraintsState(constraintsState) {
            const simplexLineup = rawLineups.getLast();
            if (!isEmpty(simplexLineup)) {
                const stackAnalyzer = new PlayerCombinationStackAnalyzer({
                    simplexLineup,
                });
                stackAnalyzer.getStackItemIds().map((stackItemId) => {
                    if (constraintsState.state.hasOwnProperty(stackItemId)) {
                        constraintsState.state[stackItemId].lineupsQuantity--;
                    }
                });
            }
        }

        function filterLimitedConstraintsState(state) {
            const filteredState = [];
            state.map((item) => {
                if (item.lineupsQuantity > 0) {
                    filteredState.push(item);
                }
            });
            return filteredState;
        }

        function createLimitedConstraintsByState(state, keyUnitPoints, keyUnitsAtLeastNumber) {
            const constraints = [];
            const usedStackItems = [];
            state.map((item) => {
                constraints.push(...item.constraints);
                usedStackItems.push(item.stackItem);
            });

            let objective = {};
            if (!isEmpty(usedStackItems)) {
                constraints.push(
                    constraintsBuilder.createStackKeyUnitsConstraint(usedStackItems, keyUnitsAtLeastNumber),
                );
                objective = constraintsBuilder.createObjective(usedStackItems, keyUnitPoints);
            }

            return {constraints, objective};
        }

        const createLimitedConstraints = function () {
            const constraintsState = this.store.get('pсs.limitedConstraintsState', {
                state: {},
                isCreated: false,
            });

            if (!constraintsState.isCreated) {
                constraintsState.state = createLimitedConstraintsState(stacking.getItems());
                constraintsState.isCreated = true;

                this.store.set('pсs.limitedConstraintsState', constraintsState);
            }

            setStackUsageInLimitedConstraintsState(constraintsState);

            const state = Object.values(constraintsState.state);
            const filteredState = filterLimitedConstraintsState(state);

            if (!isEmpty(filteredState)) {
                const keyUnitPoints = 1000;
                const keyUnitsAtLeastNumber = 1;
                const limitedConstraints = createLimitedConstraintsByState(
                    filteredState,
                    keyUnitPoints,
                    keyUnitsAtLeastNumber,
                );
                return {...limitedConstraints};
            }
            // #22921 - Players should not be forced to all lineups if min number is set less than total number of lineups.
            if (state.length > 1) {
                const keyUnitPoints = 1;
                const keyUnitsAtLeastNumber = 0;
                const limitedConstraints = createLimitedConstraintsByState(state, keyUnitPoints, keyUnitsAtLeastNumber);
                return {...limitedConstraints};
            }

            return {
                constraints: [],
                objective: {},
            };
        }.bind(this);

        const {constraints: lockedConstraints, objective: lockedObjective} = createLockedConstraints();

        const {constraints: limitedConstraints, objective: limitedObjective} = createLimitedConstraints();

        this.store.patchObj('additionalObjective', {
            playerCombinationStacking: {...lockedObjective, ...limitedObjective},
        });

        const constraints = [...lockedConstraints, ...limitedConstraints];
        // console.log("playerCombinationStacking\n", [...constraints]);
        return constraints;
    }

    playersWhoseGamesAreLocked({incomingValues = {}}) {
        const {
            sportConfig,
            lateSwap: {csvLineups},
            playersData: {games, preparedPlayers},
            lineupResolvingProcess,
        } = Action.getState();
        const {checkIfPlayerOnDoublePosition = false} = incomingValues;
        const preparedPlayersPool = preparedPlayers.groupedBy.playerId();
        const entryId = lineupResolvingProcess.getEntryId();

        if (!sportConfig.isModeLateswap()) {
            return [];
        }

        function isPlayerOnSinglePosition(playerId) {
            if (!checkIfPlayerOnDoublePosition) {
                return true;
            }
            return !(
                preparedPlayersPool.hasOwnProperty(playerId) && isPlayerOnDoublePosition(preparedPlayersPool[playerId])
            );
        }

        const getPlayersWhoseGamesAreLocked = function () {
            let playersWhoseGamesAreLocked = this.store.get('playersWhoseGamesAreLocked', {});

            if (isEmpty(playersWhoseGamesAreLocked)) {
                Object.values(preparedPlayersPool).map((player) => {
                    if (!games.isPlayerGameLocked(player)) {
                        return;
                    }

                    const {playerId} = player;

                    playersWhoseGamesAreLocked[playerId] = getSimplexUnitNames(playerId, (pos) => true);
                });

                this.store.set('playersWhoseGamesAreLocked', {...playersWhoseGamesAreLocked});
            }

            return playersWhoseGamesAreLocked;
        }.bind(this);

        const playersWhoseGamesAreLocked = getPlayersWhoseGamesAreLocked();

        if (isEmpty(playersWhoseGamesAreLocked)) {
            return [];
        }

        function getLockedPlayersFromUploadedLineups() {
            const lockedPlayers = {};

            if (!csvLineups.hasLineups()) {
                return lockedPlayers;
            }

            const lateswapCsvLineup = csvLineups.getOneByEntryId(entryId);

            if (!lateswapCsvLineup) {
                return lockedPlayers;
            }

            lateswapCsvLineup.getPlayers().map((lateswapCsvPlayer) => {
                const player = lateswapCsvPlayer.getPlayer();
                if (!games.isPlayerGameLocked(player)) {
                    return;
                }

                const {playerId} = player;

                const allowedPositions = sportConfig.getAllowedPositionsByPositionInLineup(
                    lateswapCsvPlayer.getLineupPosition(),
                );

                lockedPlayers[playerId] = getSimplexUnitNames(playerId, (pos) => {
                    return isEmpty(allowedPositions) || inArray(pos, allowedPositions);
                });
            });

            return lockedPlayers;
        }

        function getSimplexUnitNames(playerId, isPositionAllowedFunc) {
            const simplexUnitNames = [];

            if (isPlayerOnSinglePosition(playerId)) {
                simplexUnitNames.push(getSimplexUnitName(playerId));
            } else {
                preparedPlayersPool[playerId].position.map((pos) => {
                    if (!isPositionAllowedFunc(pos)) {
                        return;
                    }
                    simplexUnitNames.push(getSimplexUnitName([playerId, pos]));
                });
            }

            return simplexUnitNames;
        }

        const lockedPlayers = getLockedPlayersFromUploadedLineups();

        let rules = [];

        {
            const excludedPlayersRuleBuilder = RuleBuilder.fromExactly(0);

            Object.keys(playersWhoseGamesAreLocked).map((playerId) => {
                let simplexUnitNames = playersWhoseGamesAreLocked[playerId];

                if (lockedPlayers.hasOwnProperty(playerId)) {
                    simplexUnitNames = simplexUnitNames.filter((name) => !inArray(name, lockedPlayers[playerId]));
                }

                simplexUnitNames.map((name) => excludedPlayersRuleBuilder.addVar(name, 1));
            });

            if (excludedPlayersRuleBuilder.isRuleCreatable()) {
                rules.push(excludedPlayersRuleBuilder.createRule());
            }
        }

        {
            Object.keys(lockedPlayers).map((playerId) => {
                const simplexUnitNames = lockedPlayers[playerId];

                if (isEmpty(simplexUnitNames)) {
                    return;
                }

                const lockedPlayerRuleBuilder = RuleBuilder.fromExactly(1);

                simplexUnitNames.map((name) => lockedPlayerRuleBuilder.addVar(name, 1));

                rules.push(lockedPlayerRuleBuilder.createRule());
            });
        }

        const rulesForPositionOfPlayerThatStaysOnFlex = this.exec(
            'createRulesForPositionOfPlayerThatStaysOnFlex',
            incomingValues,
            [],
        );

        rules = [...rules, ...rulesForPositionOfPlayerThatStaysOnFlex];
        // console.log("playersWhoseGamesAreLocked\n", [...rules]);
        return [...rules];
    }

    projectionBoostRule({incomingValues: {checkIfPlayerOnDoublePosition = false, modes = []}}) {
        const {
            sportConfig,
            projectionPointsType,
            projectionBoostRule,
            lineupGenerator: {settings},
            playersData: {preparedPlayers},
        } = Action.getState();

        if (
            settings.isRelaxBuildRulesUsed() ||
            !projectionBoostRule.isEnabled() ||
            isEmpty(projectionBoostRule.getItems())
        ) {
            return [];
        }

        const constraintsBuilder = ProjectionBoostRuleConstraintsBuilderFactory.getBuilder();
        constraintsBuilder.setDependencies({
            sportConfig,
            checkIfPlayerOnDoublePosition,
            modes,
            playersIndexedById: preparedPlayers.groupedBy.playerId(),
            playersGroupedByTeamAndPosition: preparedPlayers.groupedBy.teamAndPosition(),
            projectionPointsType: projectionPointsType.getProjectionPointsType(),
        });

        let projectionBoostRuleConstraints = [];
        let projectionBoostRuleObjective = {};
        projectionBoostRule.getItems().map((item) => {
            const {constraints, objective} = constraintsBuilder.createRuleItemConstraints(item);

            projectionBoostRuleConstraints = [...projectionBoostRuleConstraints, ...constraints];
            projectionBoostRuleObjective = {...projectionBoostRuleObjective, ...objective};
        });

        this.store.patchObj('additionalObjective', {
            projectionBoostRule: {...projectionBoostRuleObjective},
        });

        return projectionBoostRuleConstraints;
    }

    limitOnePitcherPerGame({incomingValues: {checkIfPlayerOnDoublePosition = true}}) {
        const {
            playersData: {preparedPlayers},
            lineupsData: {filters},
        } = Action.getState();
        const rules = [];

        if (!filters.limitOnePitcherPerGame) {
            return rules;
        }

        const playersByGames = preparedPlayers.groupedBy.property('gameInfo');
        const playersIndexedById = preparedPlayers.groupedBy.playerId();

        Object.values(playersByGames).map((players) => {
            const pitchers = players.filter((player) => player.isPitcher(), players);

            if (isEmpty(pitchers)) {
                return;
            }

            const builder = RuleBuilder.fromAtMost(1);

            pitchers.map((pitcher) => {
                getSimplexUnitNamesByParams({
                    playerId: pitcher.getId(),
                    playersIndexedById,
                    checkIfPlayerOnDoublePosition,
                }).map((simplexUnitName) => builder.addVar(simplexUnitName, 1));
            });

            rules.push(builder.createRule());
        });

        return [...rules];
    }

    limitPositionsPerLineup({incomingValues: {limits = [], modes = []}}) {
        const {
            playersData: {preparedPlayers},
        } = Action.getState();

        const rules = [];

        const playersByPlayerId = preparedPlayers.groupedBy.playerId();
        const playersByPosition = groupObjectsByProp(Object.values(playersByPlayerId), 'positionName');

        limits.map((positionLimit) => {
            const {positionName, limit} = positionLimit;

            if (!(limit === 0 || limit > 0)) {
                return;
            }

            if (!positionName || isEmpty(playersByPosition[positionName])) {
                return;
            }

            const players = playersByPosition[positionName];

            const builder = RuleBuilder.fromAtMost(limit);

            if (isEmpty(modes)) {
                players.map((player) => {
                    const simplexUnitName = getSimplexUnitName(player.getId());
                    builder.addVar(simplexUnitName, 1);
                });
            } else {
                players.map((player) => {
                    const simplexUnitName = getSimplexUnitName(player.getId());

                    modes.map((mode) => {
                        const simplexModeUnitName = addRoleSuffix({
                            prefix: simplexUnitName,
                            role: mode,
                        });

                        builder.addVar(simplexModeUnitName, 1);
                    });
                });
            }

            builder.isRuleCreatable() && rules.push(builder.createRule());
        });

        return [...rules];
    }
}
